import { useCallback, useRef, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import store from "../State/Store/store";
import { baseDiag, baseBlockedBy } from "../utils/fsDiag";
import StorageManager from "../utils/StorageManager";
import TickerTimeout from "../utils/TickerTimeout";
import {
  setWinAnimationsPlaying,
  setResetMainGameVisualEffects,
  setCounterAnimationStarted,
  setCounterAnimationFinished,
  setCriticalAnimationPlaying,
  setExpandingWildsOccurred,
  setSkipWinAnimation,
  setUserWantsToDismissWinAnimation,
  setSkipCounterAnimation,
  setIsWaitingForUserConfirmation,
  closeAllPopups,
  setSymbolPopupBlocked,
  openTurboSuggestionPopup,
  setTurboSuggestionPending,

  setIsAnimatedWin,
  setUserInterfaceDisabled,
  setShowFreeSpinIntro,
  setShowGambleDialog,
  setShowRoyalCollectIntro,
  setShowPotCollectionIntro,
  setShowHoldAndWinIntro,
} from "../State/actions";
import {
  selectResetMainGameVisualEffects,
  selectCurrentRound,
  selectWinningLines,

  selectSpinComplete,
  selectIsSpinning,
  selectTurboSuggestionPending,
  selectFreeSpinActive,

  selectAutoPlayActive,
  selectIntroPopupActive,
  selectIntroTransitionActive,
  selectFreeSpinsReloadPopupPending,
  selectInfoPopupActive,
  selectWinAnimationsPlaying,

  selectFreeSpinsTriggered,
  selectShowGambleDialog,
  selectFreeSpinsActive,
  selectRoyalCollectTriggered,
  selectPotCollectionTriggered,
  selectReplaySessionActive,
} from "../State/selectors";
import { selectHoldAndWinIntroPending } from "../State/Slices/holdAndWinSlice";
import { SOUND_NAME } from "../sounds";

import { useReelRefs } from "../Contexts/ReelRefsContext";
import { useReelControl } from "../Contexts/ReelControlContext";
import { GAME_LAYOUT } from "../settings";
import {
  WILD_SYMBOL_ID,
  SCATTER_1_SYMBOL_ID,
  SCATTER_2_SYMBOL_ID,
} from "../SymbolData";
import {
  FEATURES_CONFIG,
  playCascadingExpandingWild,
  playFullReelExpandingWild,
  winLoops,
} from "../featuresConfig";
import { soundManager } from "../SoundManager";
import SpineAnimationData from "../utils/SpineAnimationData";
import { waitForBoardToSettle } from "../Games/Shared/waitForBoardToSettle";
import { isTurboLike } from "../utils/speedHelpers";
import { mysteryTargetsByReel } from "../Games/Shared/mysteryStaging";
import { mysteryDrama, MYSTERY_DRAMA_CLIPS } from "../Games/Shared/mysteryDrama";
import {
  goldenCellTexture,
  goldenWinSound,
} from "../Games/Main/Mystery/goldenMysteryConfig";
import {
  SCENE_ACCENTS,
  SCENE_ACCENT_TRACKS,
} from "../Components/Animations/GameViewSimple/GameViewSimpleController";
import {
  goldenOutcomeAt,
  goldenStopOrder,
  goldenShownBoard,
} from "../Games/Shared/goldenMysteryStaging";
import { runGoldenMysteryLoop } from "../Games/Shared/goldenMysteryRunner";
import {
  goldenGambleRevealCell,
  goldenPayingCells,
} from "../Games/Shared/goldenMysteryStaging";
import {
  selectPaytable,
  selectBaseBet,
  selectBet,
} from "../State/selectors";
import { symbolData,
  MYSTERY_SYMBOL_ID,
  BONUS_SYMBOL_ID } from "../SymbolData";


// "No reveal has happened for this round yet."
//
// A Symbol rather than null, for the reason useFreeSpinBasicWinController uses
// one: the main effect compares this against `currentRound?.id ?? null`, so a
// null sentinel is equal to a round with no id and the gate silently opens.
const UNREVEALED = Symbol("unrevealed");

/**
 * Frames the mystery reveal will wait for the reels to be built before giving
 * up. ~2 seconds at 60fps — far longer than a reload takes, short enough that
 * a scene which never arrives does not bump state for ever.
 */
const MYSTERY_SCENE_MAX_WAITS = 120;


// Hook for managing win line animations, sound effects, and reel coordination
export const useWinController = () => {
  const {
    reelRefs,
    overlayReelRefs,
    winLinesContainerRef,
    winBoxContainerRef,
    dimReelSetRef,
    globalLineWinTextRef,
    lineWinTextRef,
    characterAnimationControllerRef,
    gameViewControllerRef,
    potChargingAnimatorRef,
    goldenTrailAnimatorRef,
    snapClearAllRef,
  } = useReelRefs();

  const { spacebarTurboActive, spacebarTurboActiveRef } = useReelControl();

  const dispatch = useDispatch();

  const spinComplete = useSelector(selectSpinComplete);
  const isSpinning = useSelector(selectIsSpinning);
  const currentRound = useSelector(selectCurrentRound);
  const winningLines = useSelector(selectWinningLines);

  const turboSuggestionPending = useSelector(selectTurboSuggestionPending);
  const freeSpinActive = useSelector(selectFreeSpinActive);
  // ── Only act while MY board is the one on screen ──
  //
  // `freeSpinActive` above is Zeus's featureGame flag and nothing in Mad Labs
  // sets it, so the two guards below were dead. That went unnoticed until the
  // base game's entry teardown started clearing its own win dedupe: with
  // animationCompletedRef re-armed and rgsPlay.fulfilled setting base
  // spinComplete on a FEATURE response (see the wasFeatureSpin branch in
  // spinSlice), the base controller re-animated the TRIGGER round on the first
  // free spin — a win appearing before the bonus reels had stopped.
  //
  // This is the Mad Labs flag, and it is the ONE line that differs by board:
  //   base game  bail while the bonus board owns the screen
  const bonusBoardActive = useSelector(selectFreeSpinsActive);
  const resetMainGameVisualEffects = useSelector(
    selectResetMainGameVisualEffects,
  );
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const introPopupActive = useSelector(selectIntroPopupActive);
  // The boot intro's black wipe. `introPopupActive` drops when the screen is
  // fully covered — 400ms before the board can be seen — so a restored round
  // that waited only on the popup opened its scatter doors behind the black
  // and was already spinning when the game appeared. Vladimir, 2026-09-24:
  // *"we close it, then doors open."* This stays true until the fade-out ends.
  const introTransitionActive = useSelector(selectIntroTransitionActive);
  const infoPopupActive = useSelector(selectInfoPopupActive);
  // The mid-round reload popup owns the screen, or is about to. Armed in
  // freeSpinsSlice off the init rather than by the scene, so it is true on the
  // FIRST frame — the scene cannot raise its own flag until the boot intro
  // flips showGameAfterTransition, and a restored win presenting in that gap is
  // exactly what put a big win on top of the reload screen.
  const freeSpinsReloadActive = useSelector(
    selectFreeSpinsReloadPopupPending,
  );
  // PLURAL — freeSpins.triggered, the Mad Labs slice. The singular
  // featureGame.freeSpinTriggered is Zeus's and is never set in this game,
  // which is why every gate below used to be dead.
  const freeSpinTriggered = useSelector(selectFreeSpinsTriggered);
  // The gamble dialog is up, so SC1's reveal is over — finished live, or never
  // replayed after a reload straight into the decision. See the restore effect.
  const showGambleDialog = useSelector(selectShowGambleDialog);
  const royalCollectTriggered = useSelector(selectRoyalCollectTriggered);
  const potCollectionTriggered = useSelector(selectPotCollectionTriggered);


  // Refs for state management and animation control
  const reelsetControllerRef = useRef(null);
  const currentTimeoutsRef = useRef([]);
  const pendingAnimationFramesRef = useRef([]);
  const isAnimatingRef = useRef(false);
  const isReadyRef = useRef(false);
  const animationCompletedRef = useRef(false);
  // Tracks the round ID that was fast-pathed in super turbo.
  // The recovery effect uses this to undo the fast path if the user releases
  // spacebar within the same round (keyup fires AFTER the fast path committed).
  const fastPathRoundRef = useRef(null);
  const isFirstAnimationRef = useRef(true);
  const spinCompleteRef = useRef(spinComplete);
  const currentRoundRef = useRef(currentRound);
  // Round id whose reveal sequence has already run. The win display cycles its
  // lines repeatedly; a reveal must fire once for the round, not once a cycle.
  // Initialised UNDEFINED, not null. A live round with no id resolves to
  // null, and `null !== null` reads as "already played" — which is exactly how
  // the reveal silently never ran. Undefined can never equal a round id, so a
  // missing id now fails safe (it plays) instead of failing silent.
  const revealPlayedForRoundRef = useRef(undefined);
  // Rounds whose settled gamble scatter has already been painted on restore,
  // so a re-render cannot repaint one the live reveal is about to own.
  const restoredRevealRoundRef = useRef(undefined);
  // Round id whose reveal has FINISHED. Until it matches, the trigger line
  // suppresses its win animations; once it does, later passes of the cycle
  // animate normally instead of sitting silent.
  const revealDoneForRoundRef = useRef(undefined);
  // Round id whose reveal actually AWARDED something. Read off the round's own
  // reveal outcome, not off freeSpins.triggered — the award is already carried
  // on the round as gambleScatter, and the reveal config turns it into
  // `awarded`. Going through the slice meant devSimulation, which writes
  // currentRound directly and never passes through RGS, could never test the
  // win path at all.
  const revealAwardedForRoundRef = useRef(undefined);
  const winningLinesRef = useRef(winningLines);
  const winningDataRef = useRef(null);
  const lineIdsRef = useRef([]);
  const currentLineIndexRef = useRef(0);
  const dispatchRef = useRef(dispatch);

  const turboActiveRef = useRef(false);
  const infoPopupActiveRef = useRef(infoPopupActive);
  const winSoundPlayedRef = useRef(false);
  // True once THIS round's big-win celebration has been started — by show-all
  // or by a trigger branch. The trigger branches raise winAnimationsPlaying
  // for scatter-only rounds whose show-all was skipped; raised again after a
  // celebration that had already run and closed, it restarted the celebration.
  // Reset with winSoundPlayedRef at spin start.
  const celebrationRaisedRef = useRef(false);
  const expandingWildReelIndicesRef = useRef([]);
  const animatedRoundsRef = useRef(new Set());

  const executeOnReelControllersRef = useRef(null);
  const executeOnOverlayReelControllersRef = useRef(null);
  const currentIntervalsRef = useRef([]);
  const expandingWildAnimationStateRef = useRef({});
  const currentSpineListenerRef = useRef(null);
  const useFastWinRef = useRef(false); // Sticky flag: true if this spin entered with win_fast (persists into line cycling)
  const prevWinPosSetRef = useRef(null); // Previous line's winning positions for diff-based transitions


  // Memoize simple derived values
  const hasWins = useMemo(
    () => currentRound?.win && currentRound.win > 0,
    [currentRound?.win],
  );

  const hasScatterWin = (currentRound?.scatterCount || 0) > 0;


  const winType = currentRound?.winType || "";

  // ── The celebration, and the one case it is withheld ──
  //
  // `winAlreadyPresented` is set by gameSettingsSlice when it hands the live
  // round back at the end of a replay session. That round was celebrated before
  // the player opened the history, so playing the tier chain again is a second
  // fanfare for money paid minutes ago.
  //
  // Suppressed HERE and nowhere else, because this is the single place the flag
  // is derived and every `setWinAnimationsPlaying(true)` in this file is gated
  // on it. Everything else about the round is untouched: the win lines still
  // cycle, the win boxes still draw, the amount still shows and the balance
  // still updates (updateDisplayedBalance is a SYNC, not an add, so re-running
  // the presentation cannot double-credit). Only the big-win animation goes.
  const isAnimatedWin =
    (currentRound?.isAnimatedWin && !currentRound?.winAlreadyPresented) || false;



  /**
   * Clears all active timeouts, intervals, and pending animation frames.
   * Prevents memory leaks and race conditions when animations are interrupted or component unmounts.
   */
  const clearAllTimeouts = useCallback(() => {
    const timeouts = currentTimeoutsRef.current;
    for (let i = 0; i < timeouts.length; i++) {
      const t = timeouts[i];
      if (t && typeof t.clear === "function") {
        t.clear();
      } else {
        clearTimeout(t);
      }
    }

    currentTimeoutsRef.current = [];

    const intervals = currentIntervalsRef.current;
    for (let i = 0; i < intervals.length; i++) {
      const t = intervals[i];
      if (t && typeof t.clear === "function") {
        t.clear();
      } else {
        clearInterval(t);
      }
    }
    currentIntervalsRef.current = [];

    const animationFrames = pendingAnimationFramesRef.current;
    for (let i = 0; i < animationFrames.length; i++) {
      cancelAnimationFrame(animationFrames[i]);
    }
    pendingAnimationFramesRef.current = [];

    // Remove any pending spine complete listener to prevent stale resolves
    if (currentSpineListenerRef.current) {
      const { spineInstance, listener } = currentSpineListenerRef.current;
      try {
        spineInstance?.state?.removeListener(listener);
      } catch {
        // spine may already be destroyed
      }
      currentSpineListenerRef.current = null;
    }
  }, []);

  // ── Leaving a replay: hand the presentation refs back ──
  //
  // These are REFS, not Redux, so none of the slice-level restore reaches them.
  // `isAnimatingRef` in particular is latched true by the presentation that ran
  // BEFORE the player opened the history, and nothing lowers it — leaving a
  // replay is not a spin, and the visual-reset path that normally clears these
  // is not dispatched here either. The main effect below then returns at its
  // top gate on every pass, and the restored round shows no win at all.
  //
  // ── Falling edge ONLY — leaving replay, not entering it ──
  //
  // An entry-side version of this was tried on the other game and reverted.
  // Cancelling the live presentation on the way IN kills `cycleThroughLines`
  // mid-flight, and that loop is what tidies up after itself: the board was
  // left DIMMED with the boxes and the win text already gone, which reads as
  // the game having broken rather than as a replay starting. Entering a replay
  // does not need the presentation torn down — the replay paints its own board
  // over the top, and the stale win NUMBER is handled by animationsSlice on
  // `startReplaySession`.
  //
  // The CELEBRATION stays withheld on the way out: that is `winAlreadyPresented`
  // on the round, read where `isAnimatedWin` is derived above. This only
  // restores the ability to present at all.
  const replaySessionActive = useSelector(selectReplaySessionActive);
  const prevReplaySessionForWinRef = useRef(replaySessionActive);
  useEffect(() => {
    const prev = prevReplaySessionForWinRef.current;
    prevReplaySessionForWinRef.current = replaySessionActive;

    if (!prev || replaySessionActive) return;

    if (isAnimatingRef.current) {
      isAnimatingRef.current = false;
      clearAllTimeouts();
    }
    animationCompletedRef.current = false;
    isFirstAnimationRef.current = true;
    // Cleared so the restored round CAN present — leave it and the dedupe
    // still has the round marked as shown, which blocks it instead.
    animatedRoundsRef.current.clear();
  }, [replaySessionActive, clearAllTimeouts]);





  // ── Mystery (B1) reveal ─────────────────────────────────────────────────
  //
  // Runs BEFORE anything is evaluated, because that is the game's rule: "B1
  // resolves before win evaluation — the board that pays is not the board that
  // stopped" (invariant I1). It also has to run on LOSING rounds, which is why
  // it cannot live inside the win presentation: that returns early at the
  // triggerWin guard.
  //
  // The round id is held in STATE rather than a ref because the main effect
  // waits on it. A ref is invisible to React and the presentation would never
  // re-run — the same trap that has bitten this codebase six times.
  const [mysteryRevealedRoundId, setMysteryRevealedRoundId] = useState(UNREVEALED);
  const mysteryRunningRef = useRef(false);

  // ── Waiting for a scene that does not exist yet ──
  //
  // On a RELOAD this effect is the first thing in the game that is ready to
  // run: `spinComplete` defaults to true, the round comes straight out of
  // storage, and both are true before a single reel has been built. The reveal
  // then ran against nothing — `overlay?.playRevealSequence?.()` is optional
  // chained, so seven missing controllers were seven SILENT skips — and the
  // round was published as revealed, so it could never run again. The cells
  // ended up as plain symbols and the win that followed animated the mystery
  // spine, because their identity had never been rewritten either.
  //
  // The main win effect is saved from the same race by accident: it blocks on
  // `introPopupActive`, which clears only after the scene is up. This one has
  // to say so out loud. The tick is what re-runs it — a ref cannot, being
  // invisible to React, which is the trap this codebase has hit six times.
  const [mysterySceneTick, setMysterySceneTick] = useState(0);
  const mysterySceneWaitsRef = useRef(0);

  // ── How much ceremony this board earns ──
  //
  // Scored from POTENTIAL — what the board could pay if the mysteries became
  // the best symbol for it — not from what it actually paid. A board that
  // threatens a huge win deserves the long build-up whether or not it lands;
  // that tension IS the feature. See Games/Shared/mysteryDrama.
  //
  // Kept in a ref because the reveal reads it from inside an async sequence
  // that outlives the render which computed it.
  const dramaPaytable = useSelector(selectPaytable);
  const dramaLines = useSelector(selectWinningLines);
  const dramaBet = useSelector(selectBet);
  const dramaBaseBet = useSelector(selectBaseBet);
  const mysteryClipRef = useRef(MYSTERY_DRAMA_CLIPS.small);

  useEffect(() => {
    const roundId = currentRound?.id ?? null;
    const targets = mysteryTargetsByReel(currentRound);

    // ── A settled board is already revealed ──
    //
    // The trigger round of a bonus restored straight into its room: its lids
    // opened before the bonus, and the reel paints it resolved (see
    // resolveReelForMystery in Reel.jsx). Published as revealed without
    // playing, so the win controller — which waits on this — is not held.
    if (
      currentRound?.boardSettled &&
      targets.length &&
      mysteryRevealedRoundId !== roundId
    ) {
      baseDiag(`mysteryReveal[${roundId ?? "null"}] -> settled board, not replayed`);
      setMysteryRevealedRoundId(roundId);
      return;
    }

    // The mystery reveal's gates, in order. Every one used to return in
    // silence, so a round that never revealed looked the same whichever one
    // refused it. The first that blocks is logged once per evaluation — with
    // the round id, which says whether this is the restored round at all.
    // Only when the board HAS mysteries, or every spin would log a refusal.
    if (
      targets.length &&
      baseBlockedBy(`mysteryReveal[${roundId ?? "null"}]`, [
        ["bonusBoardActive", bonusBoardActive],
        ["spin not complete", !spinComplete || isSpinning],
        ["already running", mysteryRunningRef.current],
        ["already revealed", mysteryRevealedRoundId === roundId],
        // ── Not before the intro popup is gone ──
        //
        // The same wait the win presentation makes, for the same reason: a
        // reveal that plays behind the popup is a reveal the player never
        // sees. On a live spin the popup is long closed, so this costs nothing.
        ["introPopupActive", introPopupActive],
        ["introTransitionActive", introTransitionActive],
        // ── ...and not behind the free-rounds message either ──
        //
        // A reload during a free-rounds campaign puts the "free rounds left"
        // message up as the INFO popup. The reveal did not wait for it, so it
        // would play behind it and the player would close the popup onto a
        // board that had already finished. The same wait, for the same reason.
        ["infoPopupActive", infoPopupActive],
      ])
    ) {
      return;
    }
    if (!targets.length) return;

    // ── Not before the cells it reveals exist ──
    //
    // Every reel this round touches must have BOTH controllers: the overlay
    // runs the sequence, and the reel underneath takes the identity and the
    // settled texture at the end. Missing either one silently half-finishes
    // the cell.
    const reelset = reelsetControllerRef.current;
    const sceneReady =
      !!reelset &&
      targets.every(
        ({ reelIndex }) =>
          !!reelset.overlayReelControllers?.[reelIndex] &&
          !!reelset.reelControllers?.[reelIndex],
      );
    if (!sceneReady) {
      // Try again next frame. Bounded, because a scene that never arrives must
      // not leave a rAF loop bumping state for the rest of the session.
      // ── It never gives up ──
      //
      // A reveal that stops waiting is a round that never presents: the win
      // controller holds on it, so a give-up here froze the round for good.
      // Vladimir: *"it must wait until we close it, no time limit."* So the
      // fast per-frame retries are followed by a slow poll instead of an end —
      // it waits for ever, and costs one check every half second rather than a
      // state bump every frame.
      mysterySceneWaitsRef.current += 1;
      if (mysterySceneWaitsRef.current <= MYSTERY_SCENE_MAX_WAITS) {
        requestAnimationFrame(() => setMysterySceneTick((t) => t + 1));
      } else {
        if (mysterySceneWaitsRef.current === MYSTERY_SCENE_MAX_WAITS + 1) {
          baseDiag(`mysteryReveal[${roundId ?? "null"}] still waiting for the scene — slow poll`, {
            reelset: !!reelset,
            reels: targets.map((t) => t.reelIndex).join("/"),
          });
        }
        new TickerTimeout(() => setMysterySceneTick((t) => t + 1), 500);
      }
      return;
    }
    mysterySceneWaitsRef.current = 0;
    baseDiag(`mysteryReveal[${roundId ?? "null"}] -> running`, {
      cells: targets.reduce((n, t) => n + t.rows.length, 0),
      golden: !!currentRound?.storage?.coins_feature,
    });

    // The order the Golden tracks stop in, worked out once for the whole board:
    // it is a property of the round, and every cell needs to know where it sits
    // in the others. See goldenStopOrder.
    const stopOrder = goldenStopOrder(currentRound);

    const drama = mysteryDrama(currentRound, {
      paytable: dramaPaytable,
      lines: dramaLines,
      bet: dramaBet,
      baseBet: dramaBaseBet,
    });
    mysteryClipRef.current =
      MYSTERY_DRAMA_CLIPS[drama.tier] ?? MYSTERY_DRAMA_CLIPS.small;

    mysteryRunningRef.current = true;
    // Locks the UI exactly as the free-spins trigger does. Held until every
    // cell has settled, so nothing spins over a reveal in progress.
    dispatch(setCriticalAnimationPlaying(true));

    (async () => {
      // ── The lock is released in a FINALLY, and that is not defensive dressing
      //
      // This block raises criticalAnimationPlaying and holds it for the whole
      // reveal. Everything inside it is async and touches spines, textures and
      // captures; if any of it throws, the release below never runs and the game
      // is bricked — no spin button, for the rest of the session, with nothing
      // on screen to say why. The reveal failing should cost the round's
      // animation, never the session.
      try {
      // Let the landing finish its own texture→spine handover first.
      //
      // useLandingAnimations hides a landed cell's static ONE RAF after it
      // shows the spine, on purpose, so the two overlap. `spinComplete` fires
      // before that RAF has run for the last reel, so taking the cells here
      // starts a reveal over a static that is still at full alpha — the trace
      // shows reel 4 hidden three lines AFTER `reveal START`.
      await new Promise((resolve) => requestAnimationFrame(resolve));

      // ── The lamp lights while the mystery is still deciding ──
      //
      // Fired HERE, one line before every cell starts its build-up, so the
      // scene's wait and the cells' wait are the same beat.
      //
      // Fitted to the window rather than looped and cut: the clip is 800ms and
      // the two anticipations are 500ms and 2000ms, so a plain loop leaves the
      // lamp mid-flash at the exact frame the doors open. Fitted, it ends on a
      // frame the animator drew — two quick flashes for a small mystery, four
      // for a big one, which is a signal the player can read.
      //
      // ── The window is the WAIT PLUS THE REVEAL ──
      //
      // The lamp is the scene saying "something is happening", and that is
      // still true while the doors are opening — the symbol is not shown until
      // `my.reveal` ends. Stopping at the build-up left room for one more pass
      // and took it away.
      //
      // Both clips are read off the rig, and the reveal's NAME comes from the
      // sequence the cells actually run (`sequence.open`), so a config change
      // moves the window with it rather than leaving this behind.
      const mysteryReveal = symbolData[MYSTERY_SYMBOL_ID]?.reveal;
      // ── The rig is ASKED FOR, never named here ──
      //
      // This was the literal "my", which silently became the wrong rig the day
      // the mystery moved to `my`: the lamp window would have been measured
      // against clips the cells no longer play. The clip NAMES already come off
      // the config for exactly this reason — the skeleton now does too.
      const mysterySpine = symbolData[MYSTERY_SYMBOL_ID]?.spine;
      const revealClip = mysteryReveal?.sequence?.open?.[0];
      const openMs = revealClip
        ? (SpineAnimationData.getAnimationDuration(mysterySpine, revealClip) ?? 0)
        : 0;
      // ── The reveal is over when the LID has gone, not when the clip ends ──
      //
      // `fadeOutEndMs` is that moment: the doors land open and nothing of the
      // mystery is left. The clip runs on past it drawing nothing, and the
      // sequence does not wait for it, so neither does the lamp. Without a
      // stated end the fade follows the whole clip, and the two add up. See
      // RevealSymbolController's fade.
      const revealMs = mysteryReveal?.fadeOutEndMs
        ? mysteryReveal.fadeOutEndMs
        : openMs + (mysteryReveal?.fadeOutMs ?? 0);
      const anticipationMs =
        (SpineAnimationData.getAnimationDuration(
          mysterySpine,
          mysteryClipRef.current,
        ) ?? 0) + revealMs;
      // The WAIT gets the lamp, and only the lamp — every mystery, short
      // build-up or long. It says "something is being decided", which is
      // equally true of all of them, and it deliberately says nothing about
      // WHAT: the reels stay dark until the doors have actually shown gold.
      gameViewControllerRef?.current?.playAccentForWindow(
        SCENE_ACCENTS.MYSTERY_WAIT,
        anticipationMs,
      );

      // ── The scene's answer to a GOLDEN MYSTERY ──
      //
      // The reel lightning used to fire on the LONG build-up, as a second tier
      // of anticipation. It is spent here instead, on the one outcome that
      // earns it: the doors opening onto gold. A long build-up can still reveal
      // an ordinary symbol, so lighting the reels for it promised something the
      // round did not always deliver.
      //
      // Both are HELD from that moment to the end of the feature — the lid, the
      // tracks, the multipliers, the collector, the respins. Fitting them to
      // the build-up put the scene's biggest moment behind it in the dark.
      //
      // Fired from the CELL, at the frame the doors part (see onRevealed), not
      // from here. Anything sent before that lights the reels while the doors
      // are still shut and tells the player what is behind them.
      //
      // Once, not once per cell: eleven cells open together and each one calls
      // back. Restarting a held loop on every one of them would stutter it.
      //
      // Stopped in the reveal's `finally`, where the anticipation stops being
      // true — see the note there.
      let sceneLitForGolden = false;
      const litSceneForGolden = () => {
        if (sceneLitForGolden) return;
        sceneLitForGolden = true;
        const view = gameViewControllerRef?.current;
        view?.playAccent(SCENE_ACCENTS.MYSTERY_WAIT, { loop: true });
        view?.playAccent(SCENE_ACCENTS.MYSTERY_WAIT_STRONG, {
          loop: true,
          track: SCENE_ACCENT_TRACKS.REELS,
        });
      };

      // ── The gamble scatter opens AT ITS CELL, not after the loop ──
      //
      // Free spins has always done this for its `+FS` scatter: the cell that
      // lands one starts its own strip there and then, and the rest of the
      // board carries on around it. Vladimir: *"once we land on this it spins
      // for itself, while in the background the golden mystery proceeds."*
      //
      // The base game waited until the whole loop had finished. With a single
      // iteration that is only a beat late; with a collector chaining respins
      // it is minutes of the player watching a shut door on a board that has
      // moved on twice. Demo feedback, 2026-09-22.
      //
      // Started and NOT awaited, exactly as free spins does it. The promises
      // are joined once, after the loop, before the board is read.
      let scatterRevealPlayed = false;
      const scatterReveals = [];

      // ── One cell, one queue ──
      //
      // The landing's reveal is started and deliberately NOT awaited, so the
      // rest of the board carries on around it. The loop's re-roll then arrives
      // while it is still playing — two sequences on one controller, the second
      // turning a strip the first has not finished injecting. Round 1472920
      // showed it exactly: `goldenScatter inline` and `scatter drop` both fire,
      // and nothing turns.
      //
      // So everything that touches this cell is chained. The landing goes on
      // first, each re-roll behind the one before it, and the runner's own wait
      // on every turn keeps the board in step with the queue: land on blank,
      // then four re-rolls, in that order and no other.
      let scatterChain = null;
      const queueOnScatter = (work) => {
        const next = (scatterChain ?? Promise.resolve()).then(work);
        // The chain must never be a rejected promise — one failed turn would
        // otherwise take every later one with it.
        scatterChain = next.catch(() => {});
        scatterReveals.push(scatterChain);
        return next;
      };

      // ── What THIS listing is holding ──
      //
      // The server writes the cell as `[coord, "12", fs_count, fs_level]` on
      // every iteration it appears in, and those two numbers are that
      // iteration's answer: blank while it is re-rolling, the package on the
      // turn it finally awards.
      //
      // So every window that actually SPINS lands on its own cell. The round's
      // `gambleScatter` is the round's FINAL answer — read it here and the
      // first landing would show a package the player has not won yet, and the
      // re-rolls after it would be spinning onto something already on screen.
      //
      // `variants` is empty and stays empty: it is the ladder a further gamble
      // may offer, nothing in the reveal reads it, and the hand-off to the
      // gamble screen takes the ROUND's package, not this one.
      const cellPackage = (reel, row, cell) => ({
        position: [reel, row],
        value: cell?.fsCount || 0,
        tier: cell?.fsLevel || 0,
        variants: [],
      });

      // ── And what the ROUND finally settled on ──
      //
      // For the deferred path only — a scatter that never ran a sequence at
      // all, which is every reload and replay. There is no iteration being
      // presented there, so the round's own summary is the whole answer, and
      // it is preferred because a restored round carries it when the cell may
      // not have been saved with its numbers.
      const scatterPackageFor = (reel, row, cell) =>
        currentRound?.gambleScatter ?? cellPackage(reel, row, cell);

      // All cells at once, and all to the same symbol — invariant I2.
      await Promise.all(
        targets.flatMap(({ reelIndex, rows, replacementId }) => {
          const overlay = reelset?.overlayReelControllers?.[reelIndex];

          return rows.map(async (row) => {
            // ── B2: the cell becomes a respin, not a symbol ──
            //
            // A Mystery that reveals into the bonus symbol is the Golden
            // Mystery (invariant I3 — B2 can never land any other way). Nothing
            // below applies to it: there is no symbol to settle on, no
            // composite to bake and no identity to rewrite, because the board
            // already holds symbol 10 at this cell and the feature decides what
            // it pays.
            //
            // The whole cell — build-up, doors, the golden lid, the track —
            // belongs to its own controller. See playGoldenMysterySequence.
            if (replacementId === BONUS_SYMBOL_ID) {
              const goldenOutcome = goldenOutcomeAt(currentRound, reelIndex, row);
              await overlay?.playGoldenMysterySequence?.(row, {
                outcome: goldenOutcome,
                // Deliberately NOT the round's own outcomes. A track built
                // from them shows the player the answer before it lands — see
                // GOLDEN_STRIP_ITEMS.
                items: null,
                anticipationClip: mysteryClipRef.current,
                stopOrder: stopOrder.get(`${reelIndex},${row}`) ?? 0,
                onRevealed: litSceneForGolden,
              });

              // ── It landed the gamble scatter: open it, and move on ──
              //
              // The golden lid STAYS. Spines are pooled per `index_symbolId`,
              // so `scg` opens on top of `myg` rather than replacing it, and
              // the cell becomes a golden window with a scatter inside it.
              //
              // Only iteration 1 reaches here — `goldenOutcomeAt` reads the
              // first patch. A scatter that arrives on a later respin is the
              // loop's business and is still opened by the deferred block
              // after it, which is why that block stays.
              if (goldenOutcome?.symbolId === SCATTER_2_SYMBOL_ID) {
                const pkg = cellPackage(reelIndex, row, goldenOutcome);
                const canReveal =
                  overlay?.hasRevealSequence?.(SCATTER_2_SYMBOL_ID) ?? false;
                if (canReveal && pkg) {
                  scatterRevealPlayed = true;
                  // Claim the round's one reveal before it runs, so the win
                  // cycle's own block finds it played and does not open a
                  // second door on the same cell.
                  revealPlayedForRoundRef.current = currentRound?.id ?? null;
                  // First in the queue — every re-roll waits behind it.
                  queueOnScatter(() =>
                    overlay
                      .playRevealSequence(row, SCATTER_2_SYMBOL_ID, pkg)
                      // A failed reveal must not take the round with it: the
                      // package is already decided by the server and the cell
                      // still paints below.
                      .catch((error) => {
                        baseDiag("goldenScatter inline failed", {
                          cell: `${reelIndex},${row}`,
                          error: String(error),
                        });
                      }),
                  );
                }
              }

              // The picture the cell keeps is painted AFTER the loop, from the
              // board's final state — see the block below runGoldenMysteryLoop.
              // Painting it here would freeze iteration 1: a cell that started
              // as a x2 would ride the reel out as a x2 even though it ended as
              // a magnet holding a total.
              return;
            }

            // ── The mystery is a LID, not a window ──
            //
            // Nothing is injected into it. What the doors open onto is the
            // ordinary symbol standing on the reel underneath, painted there
            // by `onOpening` while the cell is still covered — the engine's
            // hidden-texture swap, the same one the expanding wild uses.
            //
            // replaceSymbolAtPosition and not setSymbolTextureOnly, because
            // the cell really does BECOME that symbol: it celebrates on its
            // own pooled spine like any other, and the next spin reads it off
            // the controller's own array. There is no composite to bake, no
            // frame to keep and no pair to park.
            //
            // How long it teases is decided per round, not per symbol: a
            // mystery that pays nothing opens at once, one that lands a Big
            // Win gets the long build-up. See Games/Shared/mysteryDrama.
            const reel = reelset?.reelControllers?.[reelIndex];

            await overlay?.playRevealSequence?.(
              row,
              MYSTERY_SYMBOL_ID,
              replacementId,
              {
                anticipationClip: mysteryClipRef.current,
                // The last frame before the doors move. Any later and they
                // part onto an empty cell; any earlier is equally safe, since
                // the lid is opaque for the whole build-up.
                onOpening: () => {
                  reel?.replaceSymbolAtPosition?.(row, replacementId);
                  reel?.showSymbolTexture?.(row);
                },
              },
            );

            // ── Hand the lid back ──
            //
            // _fadeOut has already taken the host to alpha 0, so this changes
            // nothing on screen — it hides the pooled spine, parks it and puts
            // its setup pose back, ready for the next round. The revealed
            // symbol is a plain cell from here on and needs none of it.
            overlay?.releaseRevealSymbol?.(row, MYSTERY_SYMBOL_ID);
          });
        }),
      );

        // ── Then the loop, once every cell has landed ──
        //
        // Iteration 1 is the cascade above. Everything after it — a multiplier
        // firing, a collector gathering, the cleared cells respinning — happens
        // across the whole board rather than per cell, so it runs here with the
        // reveals finished and all of them still standing.
        //
        // Returns immediately for the common case of a single iteration.
        await runGoldenMysteryLoop({
          // GameView owns the layer these bolts are drawn into — see
          // golden_trail_container. Read at fire time, not captured.
          trailAnimator: goldenTrailAnimatorRef?.current ?? null,
          round: currentRound,
          cellAt: (reel, row) =>
            reelset?.overlayReelControllers?.[reel]?.goldenCell?.(row) ?? null,
          // ── A blank gamble scatter re-rolls, in place ──
          //
          // The window is already open and stays open; only the strip turns.
          // The loop calls this with the cell the server re-listed, and the
          // package is derived from it exactly as the first landing was.
          //
          // `revealPlayedForRoundRef` is claimed here too: a scatter that first
          // appeared on a respin never went through the cell branch, so this is
          // the moment its door opened as far as the rest of the round is
          // concerned.
          onScatterDrop: (cell, spin) => {
            if (cell.symbolId !== SCATTER_2_SYMBOL_ID) return;
            const overlay = reelset?.overlayReelControllers?.[cell.reel];
            if (!overlay) return;
            const pkg = cellPackage(cell.reel, cell.row, cell);

            // ── Turn a window that is open; open one that is not ──
            //
            // A blank re-rolling has been standing open since its cell landed,
            // and only its strip may move — the doors must not shut and reopen
            // on a cell the player has been looking into. A scatter that landed
            // on a RESPIN has no window at all yet, so it gets the whole
            // sequence. `respinRevealSequence` answers false for that case,
            // which is what picks between them.
            //
            // Behind the landing, and behind every earlier re-roll. The
            // landing is started and not awaited, so without this queue the
            // first re-roll turns a strip the landing has not finished
            // injecting — two sequences on one controller.
            return queueOnScatter(async () => {
              const ran =
                (await overlay.respinRevealSequence?.(
                  cell.row,
                  SCATTER_2_SYMBOL_ID,
                  pkg,
                  spin,
                )) ||
                (await overlay.playRevealSequence?.(
                  cell.row,
                  SCATTER_2_SYMBOL_ID,
                  pkg,
                ));
              if (ran) {
                scatterRevealPlayed = true;
                revealPlayedForRoundRef.current = currentRound?.id ?? null;
              }
            }).catch((error) => {
              baseDiag("goldenScatter drop failed", {
                cell: `${cell.reel},${cell.row}`,
                error: String(error),
              });
            });
          },
        });

        // ── The inline reveals are joined HERE ──
        //
        // Nothing may read the board while a window is still deciding what is
        // in it: the paint below asks whether the scatter cell was revealed,
        // and the win cycle after it asks what the round awarded. Free spins
        // joins its `+FS` promises at the same point and for the same reason.
        //
        // Empty on every round that did not land one, so this costs nothing.
        if (scatterReveals.length) await Promise.all(scatterReveals);

        // ── THEN the gamble scatter opens, and only then does the win run ──
        //
        // GDD §7's loop ends on "Gamble Scatter present? -> start Gamble
        // Round", and §8 gives SC1 its own show: it "reveals its outcome and,
        // unless BLANK, hands off". So the full sequence for a feature that
        // dropped one is:
        //
        //   reveal all -> coins spin -> land -> multiplier -> collector ->
        //   respin -> coins land, scatter lands -> SCG OPENS AND SPINS ->
        //   lands on its outcome -> process win
        //
        // It has to be HERE, not in the win cycle where a board SC1's reveal
        // lives. That one is started with the win line on purpose — for a board
        // trigger the win and the reveal are the same moment. A feature scatter
        // is not: by then the loop has finished and the scatter is sitting
        // closed on the board, so the lid never opened and the win celebrated a
        // round that had not resolved.
        //
        // The GOLDEN LID STAYS. Spines are pooled per `index_symbolId`, so the
        // `scg` window opens on top of the `myg` one rather than replacing it —
        // the cell is a golden window with a scatter standing inside it, and
        // that is what the player must keep seeing.
        //
        // Only a scatter the FEATURE dropped. goldenGambleRevealCell is the
        // test — it finds one whatever it awarded, including a blank, because
        // the show runs either way and only the hand-off is gated.
        //
        // TRACED, because all four tests below fail silently and any one of
        // them skips the whole show. Without this the only observable is "the
        // scatter did nothing", which is identical for a round that had no
        // scatter, a round the RGS built no package for, a reel with no
        // overlay controller and a controller with no scg sequence.
        const featureScatter = goldenGambleRevealCell(currentRound);
        // ── The package, derived from the cell when the round carries none ──
        //
        // For a feature-dropped SC1 the cell IS the package: the server writes
        // `[coord, "12", fs_count, fs_level]`, and the RGS's own
        // goldenGambleRevealTarget builds `gambleScatter` out of those two
        // numbers and nothing else. So there is no information here that the
        // round does not already hold.
        //
        // Needed because `gambleScatter` is a FROZEN field on a restored round.
        // StorageManager persists whatever the spin computed, so a round saved
        // before this path existed comes back with null in it for ever, and a
        // reload of a real, kept round finds the scatter cell and no package —
        // exactly what round 1451830 showed (`featureScatter=0,1
        // gambleScatter=null`). `storage` is persisted, so the cell survives;
        // deriving from it makes the reload independent of what was saved.
        //
        // `variants` is empty on purpose. It is the pool a FURTHER gamble may
        // offer, and it only matters once a package was actually awarded — and
        // an awarded scatter enters free spins, which restores down its own
        // path with the ladder intact. What reaches here is the blank, whose
        // ladder is closed by definition.
        const scatterReveal = featureScatter
          ? scatterPackageFor(featureScatter.reel, featureScatter.row, featureScatter)
          : null;
        const scatterOverlay = featureScatter
          ? reelset?.overlayReelControllers?.[featureScatter.reel]
          : null;
        const scatterCanReveal =
          scatterOverlay?.hasRevealSequence?.(SCATTER_2_SYMBOL_ID) ?? false;
        // ── Only when the cell did not open it already ──
        //
        // Iteration 1 is handled inline, at the cell. What still reaches here
        // is a scatter that arrived on a LATER respin — `goldenOutcomeAt` reads
        // the first patch only, so the cell branch never saw it — and every
        // reload or replay, where the mystery reveal does not run at all.
        if (!scatterRevealPlayed && featureScatter && scatterReveal) {
          if (scatterCanReveal) {
            scatterRevealPlayed = true;
            // Claim the round's one reveal before awaiting, so the win cycle's
            // own block finds it already played and does not open a second
            // door on the same cell.
            revealPlayedForRoundRef.current = currentRound?.id ?? null;
            await scatterOverlay.playRevealSequence(
              featureScatter.row,
              SCATTER_2_SYMBOL_ID,
              scatterReveal,
            );
          }
        }

        // ── The picture each Golden cell keeps ──
        //
        // The feature runs on live spines and hands them back the moment the
        // next spin starts. Underneath them the reel still holds the MASKED
        // mystery texture, because a Golden cell is deliberately never given a
        // new identity — so without this the board flashes back to a row of
        // question marks and spins that away.
        //
        // Painted from the board's FINAL state, not from what first landed. A
        // cell that opened on a x2 ends as whatever respun into it once the
        // multiplier deleted itself; a coin that was multiplied and then
        // collected ends as something else again. The magnet's total and a
        // coin's multiplied value exist nowhere in the payload — they are
        // computed by goldenMysteryScript, which is what makes this possible.
        //
        // TEXTURE ONLY. The cell keeps its identity, which is what keeps the
        // ordinary win presentation off it — see the guards in
        // OverlayReelController.
        //
        // ── And only the cells that PAID keep their live spine ──
        //
        // A Golden cell's picture is a spine on the overlay layer, which draws
        // ABOVE the dim overlay. So a cell that keeps it stays lit whatever it
        // is worth, and leaving all of them awake lights the whole feature: a
        // scatter, a blank gamble scatter and a spent multiplier celebrating
        // beside the coins that actually paid.
        //
        // The texture is set for EVERY cell first — the picture is identical
        // either way — and then the non-paying ones are parked. Both
        // steps are in this one synchronous block, so the frame that loses the
        // spine is the frame the texture is already showing and there is
        // nothing to flash. See the one-layer handover rule.
        //
        // Paying is decided by the final board, the same object this loop walks:
        // money at the end, which is a coin with a value or a collector holding
        // what it swallowed.
        // ── Which cells STAY AWAKE when the feature hands over ──
        //
        // Not "which cells pay". A cell must survive the hand-over if ANY line
        // is going to light it, and the trigger line lights scatters — which
        // are not money and never will be.
        //
        // Parking them here because they did not pay was wrong twice over:
        // they went dark the instant the feature ended, and then show-all woke
        // them again a beat later. That gap is a cell visibly disappearing and
        // coming back for no reason the player can see.
        //
        // So: the money cells, plus every cell any NON-payout win names. The
        // feature's own payout is excluded because it carries all of the
        // feature's cells, paying or not — goldenPayingCells has already said
        // which of those earned it.
        const paying = goldenPayingCells(currentRound);
        for (const win of currentRound?.wins || []) {
          if (win?.isFeaturePayout) continue;
          for (const [reel, row] of win?.positions || []) {
            paying.add(`${reel},${row}`);
          }
        }
        // The cell whose scatter just ran its sequence, if one did. Its picture
        // is the OPEN window, not the closed door it arrived as — see the
        // SCATTER_2 branch in outcomeTexture. Keyed rather than flagged on the
        // board, because the board is a script of what LANDED and this is a
        // fact about what has since been presented.
        // Only when the sequence actually RAN. A cell whose reveal was refused
        // — no overlay, no scg sequence on it — never opened, so the closed
        // door is still the honest picture for it.
        // `featureScatter` is guarded as well as the flag: the inline path sets
        // the flag from iteration 1's own cell, and this reads the FINAL board.
        // Nothing should be able to open one and lose the other, and a throw
        // here would take the whole paint with it.
        const revealedKey =
          scatterRevealPlayed && featureScatter
            ? `${featureScatter.reel},${featureScatter.row}`
            : null;
        for (const cell of goldenShownBoard(currentRound).values()) {
          const key = `${cell.reel},${cell.row}`;
          const texture = goldenCellTexture(
            key === revealedKey ? { ...cell, revealed: true } : cell,
          );
          if (!texture) continue;
          const reel = reelset?.reelControllers?.[cell.reel];
          reel?.setSymbolTextureOnly?.(cell.row, MYSTERY_SYMBOL_ID, texture);
          // ── And UNCOVER it ──
          //
          // The cell's reel sprite has been at alpha 0 since the mystery
          // landed on it: an overlay spine took the cell over and
          // hideSymbolTexture put the flat picture out of the way. The
          // ordinary mystery undoes that at `onOpening` — "any later and they
          // part onto an empty cell" — but a Golden cell returns before that
          // line, because its whole presentation lives on the spine.
          //
          // setSymbolTextureOnly will not do it: it paints the texture and
          // deliberately leaves alpha alone, so "a hidden cell stays hidden".
          // So the composite was being painted onto an invisible sprite, and
          // the only thing standing between the player and an empty cell was
          // the live spine on top of it.
          //
          // That is fine for a cell that keeps its spine and fatal for one
          // that does not: parkGoldenCell hides the spine on the argument that
          // "the reel's identical composite stands in for it", and the
          // composite was not visible. Every parked cell went blank — which is
          // every scatter, every spent multiplier and every emptied collector,
          // because none of them is money and none of them is in `paying`.
          //
          // It LOOKED like it worked on a winning round only because the win
          // cycle calls showAllSymbolTextures between lines and put them back a
          // beat later. On a round that pays nothing that cycle returns at
          // `!triggerWin` and nothing ever does — so the symbols the feature
          // dropped simply vanished when the sequence ended. Mina, demo 2, and
          // Vladimir 2026-09-22: "the same symbols disappear constantly".
          //
          // The feature owns its own picture. It does not borrow the win
          // cycle's clean-up to become visible.
          reel?.showSymbolTexture?.(cell.row);
          // ── The feature hands this cell to the win cycle ──
          //
          // Every cell, paying or not: until this call nothing may park one,
          // because parking stops the spine ticking and a sequence still in
          // flight would never reach its own `complete`. See parkGoldenCell.
          //
          // A cell no win is lighting is parked in the same breath, so the
          // board reads correctly the moment the feature ends rather than at
          // the first line change.
          reelset?.overlayReelControllers?.[cell.reel]?.settleGoldenCell?.(
            cell.row,
            !paying.has(key),
          );
        }
      } catch (error) {
        // Named loudly: a silent catch here would turn a broken reveal into a
        // board that simply never opens, which is the hardest kind of bug to
        // find from the outside.
        console.error("[Mystery] reveal failed — releasing the round:", error);
      } finally {
        mysteryRunningRef.current = false;
        dispatch(setCriticalAnimationPlaying(false));
        // ── The anticipation ends where the answer begins ──
        //
        // The lamp and the reel lightning are ANTICIPATION: the scene saying
        // something is still being decided. By this line it is decided — every
        // cell has landed, the loop has run and the scatter has revealed — so
        // holding them through the win would be the scene anticipating a result
        // it is already showing.
        //
        // Here rather than at any of the exits above, because this is the one
        // place all of them meet, and it must run on a FAILED reveal too: a
        // sequence that threw would otherwise leave the scene lit for ever, and
        // nothing before the next spin would take it down.
        //
        // Safe for an ordinary mystery, which never held them. stopSceneAccents
        // skips a one-shot and skips a fitted accent already counting out its
        // passes, so this can only end something that was actually held.
        //
        // Immediately before the win is published, so the celebration's own
        // accent starts on a clear track rather than replacing a live one.
        gameViewControllerRef?.current?.stopAccent();
        // The main effect waits on this, so the win presentation starts here.
        // Published even on a failure: the round still has wins to pay, and a
        // reveal that did not finish must not also stop them being presented.
        baseDiag(`mysteryReveal[${roundId ?? "null"}] -> published`);
        setMysteryRevealedRoundId(roundId);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    spinComplete,
    isSpinning,
    currentRound,
    bonusBoardActive,
    mysteryRevealedRoundId,
    // Both are how the reload gets a second chance: the popup closing, and the
    // per-frame retry while the reels are still being built.
    introPopupActive,
    introTransitionActive,
    infoPopupActive,
    mysterySceneTick,
  ]);

  // ── A gamble scatter that has ALREADY revealed, restored ──
  //
  // The settled picture — the open gold window with the package inside it — is
  // painted in exactly one place: after playRevealSequence resolves. That is
  // right for a live round and useless for a restored one, because on a reload
  // the sequence never runs. The cell comes back as the CLOSED scatter, and the
  // player who left an open window with a level in it finds a shut door.
  //
  // ── The gate is the DIALOG, and nothing else ──
  //
  // `triggered && !gambleSettled` looked like the right test and is a trap: the
  // RGS response sets BOTH the instant it lands, which on a live trigger is
  // before the reveal has played. Gated on those, this would paint the answer
  // while the doors were still shut AND claim the round's reveal, so the live
  // sequence would never run at all.
  //
  // The gamble dialog being up is the safe signal. On a reload it means the
  // reveal will never replay — the screen came back straight into the decision.
  // On a live round it only becomes true AFTER the reveal has finished and
  // painted, so this repaints the identical picture and changes nothing.
  //
  // An INTRO reload needs none of this: the RGS rebuilds the trigger round and
  // the sequence replays in full, painting at its end exactly as it did live.
  //
  // ── A RESTORED BOARD HAS NO LIVE SPINES. Only textures. ──
  //
  // Vladimir, 2026-09-23, after this had been chased through four layers:
  // *"when we come back, WE SHOULDNT HAVE ANY SPINE ACTIVE, ALL SYMBOLS ARE
  // TEXTURE and you were showing spine all the time."* He was right, and the
  // proof was in my own probe: every other field came back null, but
  // `activeAnimations.get(row)` returned an ENTRY. An overlay spine was sitting
  // on the settled cell drawing the closed gold door over a composite that had
  // been measured correct — right texture, alpha 1, nothing else above it.
  //
  // That is why pressing SPIN fixed it: the spin's own teardown hides every
  // overlay animation, and the texture underneath was right all along.
  //
  // The cell is handed back to the reel here: the overlay animation goes, and
  // the flat picture is uncovered. Retried because on a cold load the reels are
  // built a frame or more after this runs, and the round never changes again on
  // a reload — so there is no second chance from the effect itself.
  useEffect(() => {
    if (bonusBoardActive) return;
    const settled = currentRound?.settledGambleScatter;
    if (!Array.isArray(settled?.position)) return;
    const [reelIndex, row] = settled.position;

    let done = false;
    const uncover = () => {
      if (done) return true;
      const overlay =
        reelsetControllerRef.current?.overlayReelControllers?.[reelIndex];
      const reel = reelsetControllerRef.current?.reelControllers?.[reelIndex];
      if (!overlay || !reel) return false;

      overlay.hideWinAnimation?.(row);
      reel.showSymbolTexture?.(row);
      done = true;
      return true;
    };

    uncover();
    // Belt and braces: the teardown that put this spine up can run after us.
    const timers = [200, 700, 1500].map(
      (ms) =>
        new TickerTimeout(() => {
          done = false;
          uncover();
        }, ms),
    );
    return () => timers.forEach((t) => t.clear());
  }, [bonusBoardActive, currentRound]);

  // Once per round, and it also claims the round's reveal, so the win cycle
  // finds it played and does not open a second door on the same cell.
  useEffect(() => {
    const round = currentRound;
    const gambleScatter = round?.gambleScatter;
    const position = gambleScatter?.position;
    if (!showGambleDialog) return;
    if (!Array.isArray(position)) return;

    const roundId = round?.id ?? null;
    if (restoredRevealRoundRef.current === roundId) return;

    const [reelIndex, symbolIndex] = position;
    const overlay =
      reelsetControllerRef.current?.overlayReelControllers?.[reelIndex];
    const reel = reelsetControllerRef.current?.reelControllers?.[reelIndex];
    // The reels may not be built yet on a cold load. Left unclaimed so the next
    // render tries again rather than marking the round done with nothing drawn.
    if (!overlay || !reel) return;

    const cell = overlay.getRevealStaticCell?.(SCATTER_2_SYMBOL_ID, gambleScatter);
    if (!cell) return;

    restoredRevealRoundRef.current = roundId;
    revealPlayedForRoundRef.current = roundId;
    // TEXTURE ONLY, for the same reason the live path gives: the cell is still
    // the gamble scatter, and rewriting its identity would stop it celebrating
    // with the other two scatters.
    reel.setSymbolTextureOnly?.(symbolIndex, cell.symbolId, cell.texture);
    reel.showSymbolTexture?.(symbolIndex);
  }, [currentRound, showGambleDialog, reelsetControllerRef]);

  // ── Re-arm the reveal on every spin ──
  //
  // BOTH the state and the ref, and the state is the REPLAY fix.
  //
  // This used to clear only the ref, on the reasoning that
  // mysteryRevealedRoundId "clears itself, because base rounds carry real
  // ids" — true of live play, where every spin is a new round and the id can
  // never repeat. A REPLAY replays one round: _replaySpin stamps
  // `adaptedRound.id = flow.round_id`, and the replay page loops that same
  // round. So the first pass revealed, published its id, and every pass after
  // it returned at `mysteryRevealedRoundId === roundId` — the mysteries landed
  // and simply never opened. Replaying the same round twice from history did
  // it too.
  //
  // The rising edge of isSpinning is the one moment a round is certainly
  // starting and certainly has nothing presented yet, which is exactly the
  // argument useFreeSpinBasicWinController makes for clearing both there.
  //
  // mysteryRunningRef must be cleared for a different reason: it is held for
  // the whole length of an async sequence, so a spin that starts over one
  // leaves it set for ever. The next round's reveal then returns at the ref,
  // never publishes its round id, and the main effect waits on an id that will
  // never come — the screen stops.
  //
  // Turbo and autoplay can no longer start a spin there (both now hold on
  // criticalAnimationPlaying, which this effect raises). A manual press still
  // can, and that must cost the round's reveal, not the session.
  //
  // ── The SCATTER reveal needs the same re-arm, for the same reason ──
  //
  // Both refs below hold a round id, and neither was ever cleared. The
  // reasoning was the one corrected above: a live round always carries a new
  // `flow.round_id`, so the id can never repeat and the guard clears itself.
  // A REPLAY breaks that assumption exactly as it broke it for the mystery —
  // one round id, played over and over — so the first pass claimed the round
  // and every pass after it returned at `revealPlayedForRoundRef.current ===
  // revealRoundId`: the scatters landed and the doors never opened. It is
  // reachable from the history panel ("Replay Again") and unavoidable on a
  // shared replay link, which loops the one round by design.
  //
  // Cleared to UNDEFINED, never null — see the note on the refs themselves.
  // `revealRoundId` is `currentRound?.id ?? null`, so a null sentinel would
  // equal a round that carries no id and hold the gate shut.
  //
  // Safe on the live path: the rising edge of isSpinning is before the reels
  // stop, so nothing has been revealed for the incoming round yet. The gamble
  // answers cannot disturb it either — gamble_freespins and collect_freespins
  // are DECISION commands (utils/spinCommands.js) and move no spin state, so
  // the ref a trigger round claimed survives until the next real spin.
  useEffect(() => {
    if (!isSpinning) return;
    setMysteryRevealedRoundId(UNREVEALED);
    mysteryRunningRef.current = false;
    mysterySceneWaitsRef.current = 0;
    revealPlayedForRoundRef.current = undefined;
    restoredRevealRoundRef.current = undefined;
  }, [isSpinning]);

  /**
   * Main effect: Orchestrates win animations after spin completes.
   * Handles expanding wilds, normal wins, and Big Win animations.
   */
  useEffect(() => {
    // Every one of these is a silent return, and any of them means "no win
    // text, no lines, no counter" — indistinguishable from the outside. Named
    // here for the same reason the free-spins controller names its own.
    if (
      baseBlockedBy("winController.main", [
        ["not ready", !isReadyRef.current || !reelsetControllerRef.current],
        ["!spinComplete", !spinComplete],
        ["isAnimatingRef (already presenting)", isAnimatingRef.current],
        ["animationCompletedRef", animationCompletedRef.current],
        ["introPopupActive", introPopupActive],
        ["introTransitionActive", introTransitionActive],
        ["freeSpinsReloadActive", freeSpinsReloadActive],
        // The round was presented before the bonus — a live return reaches
        // the same answer through its own dedupe. See boardSettled in RGS.
        ["board settled (played before the feature)", !!currentRound?.boardSettled],
        // Any win, not only a big one. The free-rounds messages are the info
        // popup, and a reload during a campaign opens on one: an ordinary win
        // replayed behind it was a win the player closed the popup onto after
        // it had finished. The mystery reveal waits on the same flag.
        ["infoPopupActive", infoPopupActive],
      ])
    ) {
      return;
    }

    if (baseBlockedBy("winController.main", [["bonusBoardActive", bonusBoardActive]])) {
      return;
    }

    // Hold everything until the mystery has opened. The reveal decides what is
    // on the board, so evaluating first would celebrate the wrong symbols —
    // and the effect re-runs when the id lands, which is why it is state.
    if (
      baseBlockedBy("winController.main", [
        [
          "mystery reveal in progress",
          !!mysteryTargetsByReel(currentRound).length &&
            mysteryRevealedRoundId !== (currentRound?.id ?? null),
        ],
      ])
    ) {
      return;
    }

    baseDiag("winController.main -> presenting", {
      roundId: currentRound?.id ?? null,
      win: currentRound?.win,
      triggerWin: !!currentRound?.triggerWin,
      triggerReveal: !!currentRound?.triggerReveal,
      fsTriggered: store.getState().freeSpins?.triggered,
      turbo: turboActiveRef.current,
      speed: store.getState().settings?.autoPlaySpeed,
    });

    if (!currentRound?.triggerWin || !winningLines) {

      // On no-win rounds, wait for any in-flight POT charging bolts to complete
      // before allowing autoplay to fire the next spin. Without this, autoplay
      // starts the next spin while bolts are mid-charge, canceling them visually.
      if (FEATURES_CONFIG.POT_CHARGING.ENABLED && potChargingAnimatorRef?.current) {
        const animator = potChargingAnimatorRef.current;
        if (animator._activeBolts.length > 0) {
          dispatch(setCriticalAnimationPlaying(true));
          animator.waitForAllTrails().then(() => {
            dispatch(setCriticalAnimationPlaying(false));
          });
        }
      }
      return;
    }

    const winTypeLocal = winType;
    const isAnimatedWinLocal = isAnimatedWin;
    const hasScatterWinLocal = hasScatterWin;

    const expandingWildReels =
      FEATURES_CONFIG.EXPANDING_WILDS.ENABLED && !hasScatterWinLocal
        ? currentRound.expandingWildReels || []
        : [];
    const hasExpandingWilds = expandingWildReels.length > 0;

    const expandingWildReelIndices = expandingWildReels.map(
      ({ reelIndex }) => reelIndex,
    );
    expandingWildReelIndicesRef.current = expandingWildReelIndices;

    const wins = currentRound?.wins;
    const winsHash =
      Array.isArray(wins) && wins.length > 0 ? JSON.stringify(wins) : null;
    const roundId = currentRound?.id || "unknown";
    const uniqueAnimationId = `${roundId}-${winsHash}`;

    if (animatedRoundsRef.current.has(uniqueAnimationId)) {
      // Mark animation as completed to unblock UI
      animationCompletedRef.current = true;
      return;
    }

    animatedRoundsRef.current.add(uniqueAnimationId);

    // Lock immediately to prevent effect re-entry during TickerTimeout delay
    isAnimatingRef.current = true;


    // FREE SPIN TRIGGER: Disable entire UI immediately so user can't interact
    // during win processing of a trigger round (3+ scatters)
    if (FEATURES_CONFIG.FREE_SPINS.ENABLED && freeSpinTriggered) {
      dispatch(setUserInterfaceDisabled(true));
      dispatch(setCriticalAnimationPlaying(true));
    }

    // ROYAL COLLECT TRIGGER: Disable entire UI immediately so user can't interact
    // during win processing of a trigger round (2 coins + 1 royal)
    if (FEATURES_CONFIG.ROYAL_COLLECT.ENABLED && royalCollectTriggered) {
      dispatch(setUserInterfaceDisabled(true));
      dispatch(setCriticalAnimationPlaying(true));
    }

    // POT COLLECTION TRIGGER: Same pattern — disable UI during trigger wins
    if (FEATURES_CONFIG.POT_COLLECTION.ENABLED && potCollectionTriggered) {
      dispatch(setUserInterfaceDisabled(true));
      dispatch(setCriticalAnimationPlaying(true));
    }

    // HOLD & WIN TRIGGER: Same pattern — its own slice, read from the store.
    if (selectHoldAndWinIntroPending(store.getState())) {
      dispatch(setUserInterfaceDisabled(true));
      dispatch(setCriticalAnimationPlaying(true));
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // TURBO WIN FAST PATH (HOLD OR AUTOPLAY)
    // Activates when user is actively holding spacebar/button (turbo or super turbo)
    // OR autoplay is running in super turbo mode.
    // Single-press in super turbo mode falls through to normal win processing below.
    // Skip all win animations for regular wins — show value, unblock spin immediately.
    // Animated wins (Big Win, Mega Win) and feature triggers still go through normal pipeline.
    // ═══════════════════════════════════════════════════════════════════════════
    const currentSpeed = store.getState().settings.autoPlaySpeed;
    // Read from REF (synchronous) — React state can be stale if user released
    // spacebar between spin-fire and win-processing. The ref updates instantly.
    const isActivelyHolding = spacebarTurboActiveRef.current;
    const isAutoPlaySuperTurbo =
      store.getState().settings.autoPlayActive && currentSpeed === "super_turbo";


    // ── The trigger flags, read from the STORE ──
    //
    // `freeSpinTriggered`, `royalCollectTriggered` and `potCollectionTriggered`
    // are useSelector values, and this effect can run on the same tick the
    // trigger is dispatched — before React has re-rendered with it. A stale
    // `false` here let super turbo take the fast path on a TRIGGER round: the
    // counter was marked finished, the spin unblocked, and the gamble scatter
    // went past without its reveal ever being presented. Reported on the golden
    // scatter, which is exactly this case — it triggers the GAMBLE, so free
    // spins is not `active` yet and nothing else in the list catches it.
    //
    // `triggerReveal` is included for the same reason isTriggerPresentationRound
    // includes it: a reveal round must be presented even before any slice flag
    // is set. Same test as that helper, inlined because it is declared further
    // down this hook.
    //
    // Every other speed already stops for a trigger. This is what makes super
    // turbo agree with them.
    const triggerState = store.getState();
    const isTriggerRoundNow =
      currentRound?.triggerReveal === true ||
      !!triggerState.freeSpins?.triggered ||
      !!triggerState.featureGame?.royalCollectTriggered ||
      !!triggerState.potCollection?.potCollectionTriggered ||
      selectHoldAndWinIntroPending(triggerState) ||
      freeSpinTriggered ||
      royalCollectTriggered ||
      potCollectionTriggered;

    if (
      currentSpeed === "super_turbo" &&
      (isActivelyHolding || isAutoPlaySuperTurbo) &&
      !isAnimatedWinLocal &&
      !isTriggerRoundNow &&
      !hasExpandingWilds
    ) {
      // ─────────────────────────────────────────────────────────────────────
      // SUPER TURBO FAST PATH — skip animations, show value, unblock spin.
      //
      // RACE CONDITION SAFETY: The user's keyup event may still be in transit
      // (finger physically leaving the key). If they release within ~100ms,
      // the Recovery Effect below detects the spacebarTurboActive→false
      // transition, undoes this fast path, and runs normal win processing.
      // ─────────────────────────────────────────────────────────────────────

      // Signal WinDisplay to show the final value immediately
      dispatch(setCounterAnimationStarted(true));
      dispatch(setCounterAnimationFinished(true));
      dispatch(setCriticalAnimationPlaying(false));

      // Flash WinText on the reelset (both spacebar hold and autoplay super turbo)
      if (
        FEATURES_CONFIG.WIN_LINE_DIGIT.ENABLED &&
        globalLineWinTextRef?.current &&
        currentRound?.win
      ) {
        globalLineWinTextRef.current.showWinAmount(currentRound.win, {
          skipIntro: false,
          targetDuration: 700,
        });
      }

      isAnimatingRef.current = false;
      animationCompletedRef.current = true;

      // Tag this round for potential recovery if user releases right after
      fastPathRoundRef.current = uniqueAnimationId;


      return () => clearAllTimeouts();
    }



    // CRITICAL: Block the spin button IMMEDIATELY, before the wait below
    // Without this, there's a gap where counterAnimationStarted is false
    // and the user can click spin before win processing starts
    dispatch(setCounterAnimationStarted(true));
    // Also hold WinDisplay hidden during the wait — it checks !criticalAnimationPlaying,
    // so it won't show until setCriticalAnimationPlaying(false) fires inside the body
    dispatch(setCriticalAnimationPlaying(true));

    // ── WAIT FOR THE BOARD TO BE STILL, don't guess at it ──
    //
    // This was a flat WIN_LINES_DELAY of 250ms. The landing bounces are
    // 367-667ms and every one of them is longer than that, so the win
    // presentation — which sets its clip on the SAME track 0 the bounce is
    // using — cut the landing off. Always the LAST reel, because the reels
    // stop 200ms apart and only the final one is still bouncing when the
    // delay expires. Reported 2026-09-10.
    //
    // So the signal is now the landings themselves: every overlay reel reports
    // when its own bounces have finished, and this waits for all five. A reel
    // whose symbols have NO landing clip resolves instantly and never holds the
    // others up, which is the "the last reel has no animations at all" case.
    //
    // ── The guard is not optional ──
    //
    // A pooled spine can have autoUpdate switched off mid-clip and then never
    // fire `complete` at all. Without a ceiling that is a round that never pays.
    // The ceiling is READ FROM THE SPINE DATA — the longest landing clip in the
    // game plus a frame or two — so a re-export that lengthens a bounce moves it
    // too, and nothing here is a written-down duration.
    const settleWait = () =>
      waitForBoardToSettle(
        reelsetControllerRef.current?.overlayReelControllers,
        {
          fallbackMs: FEATURES_CONFIG.WIN_ANIMATION_TIMING.WIN_LINES_DELAY,
        },
      );

    settleWait().then(async () => {
      // Wait for POT charging trails to finish before processing wins
      if (FEATURES_CONFIG.POT_CHARGING.ENABLED && potChargingAnimatorRef?.current) {
        await potChargingAnimatorRef.current.waitForAllTrails();
      }

      if (hasExpandingWilds) {
        dispatch(setExpandingWildsOccurred(true));
        dispatch(setCriticalAnimationPlaying(true));

        // All expanding wilds play the same sound
        soundManager.play(SOUND_NAME.expanding_wild);

        const animationMode = FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE;

        (async () => {
          let animationPromises = [];

          if (animationMode === "full_reel") {
            animationPromises = expandingWildReels.map(({ reelIndex }) =>
              playFullReelExpandingWild(reelIndex, reelRefs, overlayReelRefs),
            );
          } else if (animationMode === "cascade") {
            animationPromises = expandingWildReels.map(
              ({ reelIndex, wildPosition }) =>
                playCascadingExpandingWild(
                  reelIndex,
                  wildPosition,
                  reelRefs,
                  overlayReelRefs,
                  currentTimeoutsRef,
                  dispatch,
                ),
            );
          }

          // Wait for all expanding wild "appear" animations to complete (event-driven)
          if (animationPromises.length > 0) {
            await Promise.all(animationPromises);
          }

          // Hide cascade animations if needed
          if (animationMode === "cascade") {
            executeOnOverlayReelControllersRef.current(
              (overlayReelController) => {
                overlayReelController?.hideAllAnimations();
              },
            );
          }

          dispatch(setCriticalAnimationPlaying(false));

          const winningData =
            reelsetControllerRef.current.matchHorizontalWinningLines(
              winningLines,
              currentRound.wins,
              currentRound,
            );

          if (winningData && Object.keys(winningData).length > 0) {
            dispatch(setIsAnimatedWin(isAnimatedWinLocal));
            dispatch(setCounterAnimationStarted(true));

            if (FEATURES_CONFIG.EXPANDING_WILDS.SHOW_ALL_WINS_BEFORE_CYCLING) {
              if (winTypeLocal && !winSoundPlayedRef.current) {
                if (isAnimatedWinLocal) {
                  winSoundPlayedRef.current = true;
                }
              }

              if (isAnimatedWinLocal) {
                dispatch(closeAllPopups());
                dispatch(setSymbolPopupBlocked(true));
                dispatch(setWinAnimationsPlaying(true));
                celebrationRaisedRef.current = true;
              }

              const shouldProceed = await showAllWinningSymbolsOnceRef.current(
                winningData,
                true,
                expandingWildReelIndices,
                winTypeLocal,
                isAnimatedWinLocal,
              );

              if (
                shouldProceed &&
                (!spacebarTurboActiveRef.current || isTriggerPresentationRound())
              ) {
                startWinningLinesAnimationRef.current(winningData);
              } else {
                if (
                  FEATURES_CONFIG.WIN_LINES.ENABLED &&
                  winLinesContainerRef.current
                ) {
                  winLinesContainerRef.current.hideWinLine();
                }
                if (globalLineWinTextRef?.current) {
                  globalLineWinTextRef.current.hide();
                }
                // NEW - unified line win text
                if (lineWinTextRef?.current) {
                  lineWinTextRef.current.hide();
                }
              }
            }

            if (!FEATURES_CONFIG.EXPANDING_WILDS.SHOW_ALL_WINS_BEFORE_CYCLING) {
              if (isAnimatedWinLocal) {
                dispatch(closeAllPopups());
                dispatch(setSymbolPopupBlocked(true));
                dispatch(setWinAnimationsPlaying(true));
                celebrationRaisedRef.current = true;
              }
            }
          }
        })();
      } else {
        const winningData =
          reelsetControllerRef.current.matchHorizontalWinningLines(
            winningLines,
            currentRound.wins,
            currentRound,
          );

        if (!winningData || Object.keys(winningData).length === 0) {
          return;
        }

        dispatch(setCriticalAnimationPlaying(true));

        const shouldShowAllWins = FEATURES_CONFIG.SHOW_ALL_WINS.ENABLED;
        const isScatterWin = currentRound?.type === "feature";
        const skipShowAllWins =
          isScatterWin && FEATURES_CONFIG.SHOW_ALL_WINS.EXCLUDE_SCATTER;

        // Skip show-all when the only win(s) are trigger symbols (coins/scatters).
        // No point showing all + holding when we can go straight to cycling the trigger line.
        // This avoids the visual jitter from short trigger animations (coins) looping only once
        // within the wild-based display window.
        const lineIds = Object.keys(winningData);
        // Without free spins a scatter win is an ordinary win: as a "trigger"
        // it skipped show-all, and in turbo nothing released the counter.
        const isTriggerOnly = lineIds.length > 0 && lineIds.every((lineId) => {
          const data = winningData[lineId];
          return data.isCoin || (data.isScatter && FEATURES_CONFIG.FREE_SPINS.ENABLED);
        });
        const skipShowAllForTriggerOnly = shouldShowAllWins && isTriggerOnly;

        (async () => {

          dispatch(setCriticalAnimationPlaying(false));
          dispatch(setIsAnimatedWin(isAnimatedWinLocal));
          dispatch(setCounterAnimationStarted(true));

          // Trigger character "yes" animation on every win
          if (FEATURES_CONFIG.CHARACTER_ANIMATION.ENABLED) {
            characterAnimationControllerRef.current?.playAnimation("yes");
          }

          // ── The scene reacts to a BIG win ──
          //
          // `isAnimatedWin` is the celebration tiers — big / mega / supermega /
          // max — and not every win, so the rat is not zapped for 0.20.
          //
          // On its own track, so the scene's 6.7s idle keeps running
          // underneath and only the rat's bones are taken over. See
          // GameViewSimpleController.playAccent.
          //
          // Fired here rather than from the celebration itself, for the same
          // reason the character's "yes" is: this is the one place a
          // presentation starts, and it runs once per round — the block it sits
          // in is entered once, behind the animatedRoundsRef guard.
          //
          // HELD, not one-shot. The win is on screen for as long as it takes to
          // count out and cycle its lines, so the reaction lasts as long as the
          // thing it is reacting to. Released by stopAccent on the next spin's
          // cleanup, and when the room takes the screen — the two places that
          // clear the board.
          //
          // An ORDINARY win gets the other variant, HELD the same way — the
          // reaction lasts as long as what it is reacting to. Both branches are
          // released by the same stopAccent calls.
          //
          // ── A PAID win only. The trigger is NOT reacted to here ──
          //
          // This used to also fire on `freeSpins.triggered`, which raised the
          // rat the instant three scatters landed. That is too early to mean
          // anything: the scatters then spin their strip, and a strip can land
          // on a BLANK and award nothing. The scene was celebrating a package
          // the player had not won yet, and on a blank it celebrated one they
          // never won at all.
          //
          // The trigger's reaction now fires where the trigger is actually
          // decided — with the three scatters' own win animation, in the
          // `revealAwarded` block further down. See the note there.
          //
          // A trigger that also PAYS still reacts here and immediately, as any
          // paying round does, and the block below leaves it alone rather than
          // restarting it. The two sites are complementary on `win`, which is
          // what keeps them from firing twice for one round.
          if (isAnimatedWinLocal) {
            gameViewControllerRef?.current?.playAccent(SCENE_ACCENTS.BIG_WIN, {
              loop: true,
            });
          } else if ((currentRound?.win ?? 0) > 0) {
            gameViewControllerRef?.current?.playAccent(SCENE_ACCENTS.WIN, {
              loop: true,
            });
          }

          let shouldProceed = true;

          if (shouldShowAllWins && !skipShowAllWins && !skipShowAllForTriggerOnly) {
            if (winTypeLocal && !winSoundPlayedRef.current) {
              if (isAnimatedWinLocal) {
                winSoundPlayedRef.current = true;
              }
            }

            if (isAnimatedWinLocal) {
              dispatch(closeAllPopups());
              dispatch(setSymbolPopupBlocked(true));
              dispatch(setWinAnimationsPlaying(true));
              celebrationRaisedRef.current = true;
            }

            shouldProceed = await showAllWinningSymbolsOnceRef.current(
              winningData,
              false,
              [],
              winTypeLocal,
              isAnimatedWinLocal,
            );
          }


          if (shouldProceed) {
            // When show-all is skipped, release the animation lock manually
            // (showAllWinningSymbolsOnce normally does this at its end)
            if (!shouldShowAllWins || skipShowAllWins || skipShowAllForTriggerOnly) {
              isAnimatingRef.current = false;
            }
            // During turbo/autoplay: skip per-line cycling — show-all is sufficient.
            // Autoplay continuation will snap-clear and fire next spin.
            //
            // ...unless the round triggers a feature. The cycle owns the
            // reveal and the feature screen, so skipping it is what left a
            // bonus won on a held spacebar frozen on the board.
            if (!turboActiveRef.current || isTriggerPresentationRound()) {
              startWinningLinesAnimationRef.current(winningData);
            } else {
              // Clean up show-all visuals so autoplay can proceed cleanly
              if (
                FEATURES_CONFIG.WIN_LINES.ENABLED &&
                winLinesContainerRef.current
              ) {
                winLinesContainerRef.current.hideWinLine();
              }
              if (globalLineWinTextRef?.current) {
                globalLineWinTextRef.current.hide();
              }
              if (lineWinTextRef?.current) {
                lineWinTextRef.current.hide();
              }
            }
          } else {
            if (
              FEATURES_CONFIG.WIN_LINES.ENABLED &&
              winLinesContainerRef.current
            ) {
              winLinesContainerRef.current.hideWinLine();
            }
            if (globalLineWinTextRef?.current) {
              globalLineWinTextRef.current.hide();
            }
            // NEW - unified line win text
            if (lineWinTextRef?.current) {
              lineWinTextRef.current.hide();
            }
          }

          if (!shouldShowAllWins || skipShowAllWins || skipShowAllForTriggerOnly) {
            // ── Play win sound for non-animated wins when show-all is skipped ──
            //
            // NOT withheld any more. The chime used to be deferred to the end
            // of the reveal because it was the only sound in the sequence, and
            // firing it here read as the doors' own noise. The doors have their
            // own sound now, so the order is simply played in order: the
            // regular symbols win FIRST, on its own, and the reveal cuts it
            // when the doors open — see `cutSound` in gambleScatterConfig.
            // The Golden Mystery's own chime when the feature paid, the
            // ordinary one otherwise — resolved from the data, not a flag.
            // Same `!isAnimatedWinLocal` gate either way: a win that earned a
            // banner plays the banner instead.
            if (!isAnimatedWinLocal) {
              const winChime = goldenWinSound(winningData);
              if (winChime) soundManager.play(winChime);
            }
            if (isAnimatedWinLocal) {
              dispatch(closeAllPopups());
              dispatch(setSymbolPopupBlocked(true));
              dispatch(setWinAnimationsPlaying(true));
              celebrationRaisedRef.current = true;
            }
          }
        })();
      }
    });

    return () => {
      // Only clear timeouts when cycling is NOT in progress.
      // When infoPopupActive changes (info popup close), React re-runs this effect.
      // If cycling IS running (isAnimatingRef.current = true), clearing would kill it.
      // startWinningLinesAnimation already calls clearAllTimeouts on entry,
      // so skipping here is always safe for new spin starts.
      if (!isAnimatingRef.current) {
        clearAllTimeouts();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    spinComplete,
    currentRound,
    winningLines,
    reelRefs,
    overlayReelRefs,
    isSpinning,
    dispatch,
    introPopupActive,
    introTransitionActive,
    infoPopupActive,
    freeSpinsReloadActive,
    mysteryRevealedRoundId,
  ]);

  // ═══════════════════════════════════════════════════════════════════════════
  // FAST PATH RECOVERY EFFECT
  //
  // Watches spacebarTurboActive transitions from true→false. If a fast path
  // was committed for the current round (fastPathRoundRef is set), this means
  // the user released spacebar AFTER the fast path already skipped animations.
  //
  // Recovery:
  // 1. Undo the fast path flags (animationCompleted, animatedRounds dedup)
  // 2. Reset counter animation states
  // 3. Run normal win processing with full animations
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    // Only act on deactivation: spacebar released OR autoplay ended.
    // If BOTH are still active, no recovery needed — fast path is valid.
    if (spacebarTurboActive || autoPlayActive) {
      return;
    }

    // Check if a fast path was committed for a round that hasn't advanced
    const recoveryTag = fastPathRoundRef.current;
    if (!recoveryTag) {
      return;
    }

    // Clear the tag immediately to prevent double-recovery
    fastPathRoundRef.current = null;

    // Only recover if we're not already spinning the next round
    if (isSpinning) {
      return;
    }


    // 1. Undo dedup so the round can be re-processed
    animatedRoundsRef.current.delete(recoveryTag);
    animationCompletedRef.current = false;
    isAnimatingRef.current = true; // Lock to prevent race with main effect

    // 2. Reset counter states that the fast path set
    dispatch(setCounterAnimationStarted(false));
    dispatch(setCounterAnimationFinished(false));

    // 3. Hide the fast-path WinText flash
    if (globalLineWinTextRef?.current) {
      globalLineWinTextRef.current.hide();
    }

    // 4. Run normal win processing
    if (!currentRound?.wins || !winningLines) {
      isAnimatingRef.current = false;
      animationCompletedRef.current = true;
      return;
    }

    const winningData =
      reelsetControllerRef.current.matchHorizontalWinningLines(
        winningLines,
        currentRound.wins,
        currentRound,
      );

    if (!winningData || Object.keys(winningData).length === 0) {
      isAnimatingRef.current = false;
      animationCompletedRef.current = true;
      return;
    }

    const winTypeLocal = currentRound?.winType || null;
    const isAnimatedWinLocal = currentRound?.isAnimatedWin || false;

    dispatch(setCriticalAnimationPlaying(false));
    dispatch(setIsAnimatedWin(isAnimatedWinLocal));
    dispatch(setCounterAnimationStarted(true));

    if (FEATURES_CONFIG.CHARACTER_ANIMATION.ENABLED) {
      characterAnimationControllerRef.current?.playAnimation("yes");
    }

    const shouldShowAllWins = FEATURES_CONFIG.SHOW_ALL_WINS.ENABLED;
    const isScatterWin = currentRound?.type === "feature";
    const skipShowAllWins =
      isScatterWin && FEATURES_CONFIG.SHOW_ALL_WINS.EXCLUDE_SCATTER;

    (async () => {
      let shouldProceed = true;

      if (shouldShowAllWins && !skipShowAllWins) {
        if (winTypeLocal && !winSoundPlayedRef.current) {
          if (isAnimatedWinLocal) {
            winSoundPlayedRef.current = true;
          }
        }

        if (isAnimatedWinLocal) {
          dispatch(closeAllPopups());
          dispatch(setSymbolPopupBlocked(true));
          dispatch(setWinAnimationsPlaying(true));
          celebrationRaisedRef.current = true;
        }

        shouldProceed = await showAllWinningSymbolsOnceRef.current(
          winningData,
          false,
          [],
          winTypeLocal,
          isAnimatedWinLocal,
        );
      }

      if (shouldProceed) {
        if (!shouldShowAllWins || skipShowAllWins) {
          isAnimatingRef.current = false;
        }
        startWinningLinesAnimationRef.current(winningData);
      } else {
        if (
          FEATURES_CONFIG.WIN_LINES.ENABLED &&
          winLinesContainerRef.current
        ) {
          winLinesContainerRef.current.hideWinLine();
        }
        if (globalLineWinTextRef?.current) {
          globalLineWinTextRef.current.hide();
        }
        if (lineWinTextRef?.current) {
          lineWinTextRef.current.hide();
        }
      }

      if (!shouldShowAllWins || skipShowAllWins) {
        dispatch(setCounterAnimationFinished(true));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spacebarTurboActive, autoPlayActive]);

  /**
   * Executes a callback function on each reel controller.
   */
  const executeOnReelControllers = useCallback(
    (callback) => {
      const reels = reelRefs.current;
      if (!reels) return;

      for (let i = 0; i < reels.length; i++) {
        const controller = reels[i]?.getReelController();
        if (controller) {
          callback(controller, i);
        }
      }
    },
    [reelRefs],
  );

  /**
   * Executes a callback function on each overlay reel controller.
   */
  const executeOnOverlayReelControllers = useCallback(
    (callback) => {
      const overlayReels = overlayReelRefs.current;
      if (!overlayReels) return;

      for (let i = 0; i < overlayReels.length; i++) {
        const controller = overlayReels[i]?.getOverlayReelController();
        if (controller) {
          callback(controller, i);
        }
      }
    },
    [overlayReelRefs],
  );

  useEffect(() => {
    executeOnReelControllersRef.current = executeOnReelControllers;
    executeOnOverlayReelControllersRef.current =
      executeOnOverlayReelControllers;
  }, [executeOnReelControllers, executeOnOverlayReelControllers]);

  /**
   * Resets all visual effects and animations to their default state.
   * @param {boolean} instant - If true, quick sequential fade before cleanup (used on spin start)
   */
  const resetAllEffects = useCallback(async (instant = false) => {
    // Step 1: Fade dim overlay FIRST — must be gone before we mutate layers underneath
    if (dimReelSetRef.current) {
      await dimReelSetRef.current.undimReels(instant);
    }

    // Step 2: Now safe to clear win overlays and restore textures
    if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
      winLinesContainerRef.current.hideWinLine();
    }

    if (FEATURES_CONFIG.WIN_BOX.ENABLED && winBoxContainerRef.current) {
      winBoxContainerRef.current.hideWinBoxes();
    }

    executeOnOverlayReelControllersRef.current((overlayReelController) => {
      if (overlayReelController) {
        if (overlayReelController.hideAllAnimations) {
          overlayReelController.hideAllAnimations();
        }

        if (
          FEATURES_CONFIG.EXPANDING_WILDS.ENABLED &&
          overlayReelController.isExpandingWildVisible
        ) {
          overlayReelController.forceStopExpandingWildFullReel?.();
        }
      }
    });

    executeOnReelControllersRef.current((controller) => {
      if (controller && controller.showAllSymbolTextures) {
        controller.showAllSymbolTextures();
      }
    });

    // NEW - unified line win text
    if (lineWinTextRef?.current) {
      lineWinTextRef.current.hide();
    }

    // NOTE: globalLineWinTextRef is intentionally NOT hidden here.
    // It self-cleans via its spine "out" animation → _finalizeOut().
    // If a new win arrives, showWinAmount() replaces it seamlessly.

    // Clear previous win positions for diff tracking
    prevWinPosSetRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalLineWinTextRef, lineWinTextRef]);

  /**
   * Matches winning line data with game layout winning lines.
   * Groups multiple wins on the same line together and returns structured data for animation.
   */
  /**
   * Should the dim be held back until the reveal has decided?
   *
   * ── Two rules that were being applied as one ──
   *
   * A TRIGGER-ONLY round has nothing to show until the strip lands: dimming
   * when three scatters arrive presents a win before anything has been won,
   * and on a BLANK it presents one that never comes. So it waits, and the
   * reveal block dims only once a package was actually awarded.
   *
   * A round that also PAID is a different statement. The payline win is real,
   * it is already on screen, and it earns its dim the moment it is presented —
   * exactly as any ordinary win does. It then simply stays on through the
   * reveal and the feature screen, one continuous presentation, blank or not.
   *
   * The trigger rule was being applied to both, so a genuine win was shown
   * undimmed and the board only darkened later, if at all.
   *
   * `win > 0` is the test because the trigger spin itself pays nothing — the
   * package is spins, not a win — so a non-zero win can only have come from a
   * payline.
   */
  const dimHeldForReveal = useCallback((round) => {
    if ((round?.win ?? 0) > 0) return false;
    return (
      round?.triggerReveal === true &&
      revealDoneForRoundRef.current !== (round?.id ?? null)
    );
  }, []);

  /**
   * Does this round owe the player a TRIGGER PRESENTATION?
   *
   * ── Why the turbo shortcuts have to ask ──
   *
   * Turbo skips per-line cycling on the argument that show-all already said
   * everything and autoplay will snap-clear and spin on. That holds for an
   * ordinary win. It is false here: the line cycle is the only place the
   * reveal is launched and the only place the feature screen is scheduled, so
   * skipping it strands the round holding criticalAnimationPlaying with no
   * gamble screen and no way forward.
   *
   * ── Why the award flags are not enough ──
   *
   * `triggerReveal` FIRST, and it is the one that matters. The other three are
   * "a package was won"; this one is "the full trigger board is on screen" —
   * two standard scatters and the golden one — which the provider stamps from
   * collectAllTriggerPositions, the same test that injects the synthetic
   * scatter win.
   *
   * They are deliberately different facts. SC1 can open onto a BLANK: the show
   * runs in full and nothing is awarded (GDD §8), so freeSpins.triggered is
   * never raised and an award-only test reads false on a round that owes the
   * entire sequence. Measured live — roundId 1428248 presented with
   * triggerReveal=true and fsTriggered=false, the cycle was skipped, and the
   * round died holding the screen.
   *
   * Pot Collection got away with a flag because it raises
   * potCollectionTriggered on the response itself. Free spins cannot: whether
   * anything was won is only known once the strip lands, which is after the
   * presentation this gate decides to run.
   */
  const isTriggerPresentationRound = useCallback(() => {
    if (currentRoundRef.current?.triggerReveal === true) return true;
    const s = store.getState();
    return !!(
      s.freeSpins?.triggered ||
      s.featureGame?.royalCollectTriggered ||
      s.potCollection?.potCollectionTriggered ||
      selectHoldAndWinIntroPending(s)
    );
  }, []);

  /**
   * The order the win cycle presents in: the WIN first, the TRIGGER last.
   *
   * ── Why this had to become a rule ──
   *
   * A trigger entry is not a win. It pays 0 and exists only so the scatters
   * animate and the feature screen has something to open from — see
   * buildSyntheticScatterWin. Presenting it among the paylines mixes two
   * different statements: "you won this much", and "you are going to the
   * bonus". The player should be told them in that order, and finish the first
   * before the second starts, because the second takes the screen away.
   *
   * Until now the order was whatever `wins` arrived in. Live that happened to
   * be right — the provider appends the synthetic scatter last — but nothing
   * said so, and the reload path proved it: rebuilding the round with only the
   * scatter left the cycle with a trigger and no win at all.
   *
   * Stable within each group, so paylines keep the server's own order.
   *
   * @param   {object} winningData keyed by index into round.wins
   * @returns {string[]} keys, wins first, triggers last
   */
  const orderLineIds = useCallback((winningData) => {
    const keys = Object.keys(winningData || {});
    const isTrigger = (key) => {
      const data = winningData[key];
      return !!(data?.isScatter || data?.isCoin);
    };
    return [
      ...keys.filter((key) => !isTrigger(key)),
      ...keys.filter(isTrigger),
    ];
  }, []);

  const matchHorizontalWinningLines = useCallback((winningLines, wins, round) => {
    const result = {};
    if (!Array.isArray(wins)) {
      return result;
    }

    // ── Only the cells that PAY are lit ──
    //
    // The Golden Mystery's payout carries the feature's own cells, all of
    // them, so a scatter or a blank gamble scatter arrives in the same list as
    // the coins. `positions` is what the show-all reads to decide which spines
    // wake and which cells keep a static texture under the dim, so unfiltered
    // it celebrates the whole board and says every cell paid.
    //
    // Computed ONCE per call rather than per win: the board is the same for
    // every entry, and building it is a full replay of the feature's script.
    //
    // Lazily, though — a round with no feature must not pay for it. Null means
    // "not asked yet"; an empty set means "asked, and nothing pays", which is
    // the one case where the filter is skipped rather than applied. Wiping
    // every position would leave a paid win with nothing on screen at all, and
    // showing too much is the better failure.
    let payingCells;
    const payingCellsFor = () => {
      if (payingCells === undefined) payingCells = goldenPayingCells(round);
      return payingCells;
    };

    for (let i = 0; i < wins.length; i++) {
      const win = wins[i];
      const { id, symbol, win: winAmount } = win;
      let { positions } = win;

      if (!Array.isArray(positions)) {
        continue;
      }

      if (win.isFeaturePayout) {
        const paying = payingCellsFor();
        if (paying.size > 0) {
          positions = positions.filter(([reel, row]) =>
            paying.has(`${reel},${row}`),
          );
        }
      }

      if (!result[i]) {
        result[i] = {
          line: id != null ? winningLines[id] : null, // Scatter wins have no line (id is null)
          positions: [],
          symbol,
          win: winAmount,
          lineId: id, // Preserve original line index for win_line spine animation
          isScatter: !!win.isScatter, // Preserve scatter flag from RGS
          isCoin: !!win.isCoin, // Preserve coin trigger flag from RGS
          // A feature's own payout — the Golden Mystery's. Paid, never
          // decorated. See the RGS branch that sets it.
          isFeaturePayout: !!win.isFeaturePayout,
        };
      }
      result[i].positions.push(...positions);
    }

    return result;
  }, []);

  /**
   * Syncs refs with current state values for use in async operations.
   */
  useEffect(() => {
    spinCompleteRef.current = spinComplete;
    currentRoundRef.current = currentRound;
    winningLinesRef.current = winningLines;
    dispatchRef.current = dispatch;

    turboActiveRef.current = spacebarTurboActive || autoPlayActive;
    infoPopupActiveRef.current = infoPopupActive;
  }, [
    spinComplete,
    currentRound,
    winningLines,
    dispatch,

    spacebarTurboActive,
    autoPlayActive,
    infoPopupActive,
  ]);

  /**
   * Initializes the reelset controller object once on component mount.
   * Creates lazy-loaded getters for controllers and attaches utility methods.
   */
  useEffect(() => {
    if (reelsetControllerRef.current) {
      return;
    }

    reelsetControllerRef.current = {
      _reelControllers: null,
      _overlayReelControllers: null,

      // ── Cached only once EVERY reel has answered ──
      //
      // These used to cache whatever the first read found, for the rest of the
      // session. On a reload something reads them at mount, before the last
      // reel has built its controllers — so the list froze with four entries,
      // `overlayReelControllers[4]` was undefined for ever, and every retry
      // re-read the same frozen list. The mystery reveal waited on reel 4 until
      // it gave up, and the win waited on the reveal for good. Found on a
      // free-rounds reload, 2026-09-24: `GAVE UP waiting for the scene —
      // reelset=true reels=4`.
      //
      // An incomplete answer is returned but NOT kept, so the next read asks
      // again. (Its `.filter(Boolean)` also shifts indexes when a reel in the
      // middle is missing — one more reason it must never be the cached one.)
      get reelControllers() {
        if (!this._reelControllers) {
          const all =
            reelRefs.current?.map((reel) => reel?.getReelController()) || [];
          const found = all.filter(Boolean);
          if (!all.length || found.length !== all.length) return found;
          this._reelControllers = found;
        }
        return this._reelControllers;
      },

      get overlayReelControllers() {
        if (!this._overlayReelControllers) {
          const all =
            overlayReelRefs.current?.map((overlayReel) =>
              overlayReel?.getOverlayReelController(),
            ) || [];
          const found = all.filter(Boolean);
          if (!all.length || found.length !== all.length) return found;
          this._overlayReelControllers = found;
        }
        return this._overlayReelControllers;
      },

      executeOnReelControllers,
      executeOnOverlayReelControllers,
      resetAllEffects,
      matchHorizontalWinningLines,

      /**
       * Synchronous full visual cleanup — everything in ONE frame, no async.
       * Called by handleSpin before reels start so no previous-win artifacts remain.
       */
      snapClearAll: ({ keepGoldenCells = false } = {}) => {
        // Kill async win cycling loop — prevents re-dimming after clear
        isAnimatingRef.current = false;
        // ── The scene's reaction is part of the win, so it goes with it ──
        //
        // The rat is played with `loop`, which means it is HELD until someone
        // stops it — the module says the caller owns that call. Both existing
        // stopAccent sites are in this hook (spin start, and the room taking
        // the screen), so a teardown that comes from anywhere else left it
        // running: a lost gamble strips the board through this function and
        // the rat kept zapping over a cleared screen.
        //
        // Belongs here rather than at that one call site, because this IS the
        // "everything the win put up, gone in one frame" function, and every
        // future caller gets it for free. Safe to double up — stopSceneAccents
        // ignores a track that is already mixing out.
        gameViewControllerRef?.current?.stopAccent();
        // Dim overlay
        if (dimReelSetRef.current?.snapClear) {
          dimReelSetRef.current.snapClear();
        }
        // Win lines
        if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
          winLinesContainerRef.current.hideWinLine();
        }
        // Win boxes
        if (FEATURES_CONFIG.WIN_BOX.ENABLED && winBoxContainerRef.current) {
          winBoxContainerRef.current.hideWinBoxes();
        }
        // Overlay reel animations
        executeOnOverlayReelControllersRef.current((oc) => {
          if (oc?.hideAllAnimations) oc.hideAllAnimations();
          // Golden cells are the one thing hideAllAnimations deliberately only
          // PARKS — it has no way to know the round is over. This is that
          // moment: the board is about to become something else and the reel's
          // composite override goes with it.
          //
          // ── Unless the SAME round is about to be presented again ──
          //
          // A lost gamble tears the board down through here and then hands
          // the round back as an ordinary win (keepLostRoundMoney). Its
          // golden coins still have to celebrate on their line, and a golden
          // cell celebrates through its own controller — showWinAnimation
          // routes on hasGoldenMystery. With the controllers gone, the cell
          // fell through to the ordinary path on its symbol id, which is the
          // MYSTERY: the lid spine played, doors shut, over every paying coin.
          // Vladimir, 2026-09-24: *"why only on loss we get doors."* Parked by
          // hideAllAnimations above, the cells wait for their line exactly as
          // they do after a live feature.
          if (!keepGoldenCells) oc?.clearGoldenMysteries?.();
          if (FEATURES_CONFIG.EXPANDING_WILDS.ENABLED && oc?.isExpandingWildVisible) {
            oc.forceStopExpandingWildFullReel?.();
          }
        });
        // Restore symbol textures
        executeOnReelControllersRef.current((c) => {
          if (c?.showAllSymbolTextures) c.showAllSymbolTextures();
        });
        // Line win text (per-line, not the global total)
        if (lineWinTextRef?.current) lineWinTextRef.current.hide();
        // NOTE: globalLineWinTextRef is intentionally NOT cleared here.
        // It self-cleans via its spine "out" animation → _finalizeOut().
        // This allows WinText to persist into the next spin (overlapping reels),
        // matching reference game behavior. If a new win arrives before "out"
        // finishes, showWinAmount() replaces it seamlessly.
        // Win position tracking
        prevWinPosSetRef.current = null;
      },
    };

    isReadyRef.current = true;

    // Register the synchronous cleanup on the shared ref so
    // ReelControlContext.handleSpin can call it before starting reels.
    snapClearAllRef.current = reelsetControllerRef.current.snapClearAll;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Cycles through winning lines one by one, displaying animations and win amounts.
   * Shows win text, restores textures for non-winning symbols, and plays animations for winning symbols.
   */
  const cycleThroughLines = useCallback(
    async (
      winningData,
      lineIds,
      isFirstEverCycle = false,
      startIndex = 0,
      onFirstLineComplete = null,
    ) => {
      // ── Did this pass PLAY anything? ──
      //
      // Every `continue` below leaves the loop without awaiting, so a pass can
      // finish in zero time. runSeamlessAnimationCycle repeats this call for as
      // long as isAnimatingRef is set, and a pass that never awaits turns that
      // into a tight loop that starves the frame — the tab locks with no logs,
      // because nothing ever yields to the renderer.
      //
      // Returning the answer is what lets the caller stop. It is the only
      // honest test: "are there lines" is not the same question, since a line
      // can be skipped for reasons only this loop knows.
      let playedAny = false;

      for (let index = startIndex; index < lineIds.length; index++) {
        if (!isAnimatingRef.current) {
          break;
        }

        const lineId = lineIds[index];
        currentLineIndexRef.current = index;
        const data = winningData[lineId];

        if (!data) {
          continue;
        }

        // ── A BLANK TRIGGER ENTRY LOSES ITS TURN ONCE ITS DOORS HAVE OPENED ──
        //
        // The live game appends a synthetic scatter win for any complete
        // trigger board, blank included — it carries the positions the doors
        // are opened from. Once they HAVE opened there is nothing left in it:
        // no win, no celebration. Leaving it in the rotation gave it a turn
        // that showed nothing, and the paying lines went dark for its slot.
        //
        // Kept for the pass that reveals, because a BOARD-landed scatter's
        // doors are opened from this very entry. `revealPlayedForRoundRef` is
        // set by both openers — the cycle's own and the Golden Mystery block —
        // so a feature-dropped blank is skipped from the first pass, which is
        // right: its doors were opened before the cycle began.
        if (
          data.isScatter &&
          revealPlayedForRoundRef.current === (currentRoundRef.current?.id ?? null)
        ) {
          const skipPackage = currentRoundRef.current?.gambleScatter;
          const skipBlank =
            !!skipPackage &&
            (skipPackage.value ?? 0) <= 0 &&
            (skipPackage.tier ?? 0) <= 0;
          if (skipBlank) continue;
        }

        // ── ONE AT A TIME: the scatter line waits for the big win to close ──
        //
        // A trigger that also paid a big win opened its doors and spun its
        // strip BEHIND the celebration popup: show-all raised
        // winAnimationsPlaying and the cycle went straight on to the scatter
        // line, so the reveal ran where nobody could watch it, live and on
        // reload alike. Only the FEATURE SCREEN waited for the popup (see
        // waitForAnimatedWin below); the sequence in front of it did not.
        // Vladimir, 2026-09-24: *"such an important sequence must not ever
        // happen in the background, ONE AT A TIME."*
        //
        // So the whole scatter line — the three scatters, the doors, the strip
        // — holds until the popup has been dismissed. Same 100ms TickerTimeout
        // poll as the feature-screen wait, on currentTimeoutsRef so a spin
        // clears it. An ordinary round never raises the flag and never waits.
        if (data.isScatter) {
          while (
            isAnimatingRef.current &&
            selectWinAnimationsPlaying(store.getState())
          ) {
            await new Promise((resolve) => {
              currentTimeoutsRef.current.push(new TickerTimeout(resolve, 100));
            });
          }
          if (!isAnimatingRef.current) break;
        }

        // One tick per line as the cycle steps onto it. Scatter carries a null
        // lineId and is skipped — it is announced by its own sound, not by this.
        //
        // NOT gated on WIN_LINES.ENABLED, which is what it used to be and is
        // why this never made a sound: that flag turns on the drawn LINE
        // graphics, and this game draws win BOXES instead, so it is false here.
        // The cycle runs either way, and the sound belongs to the cycle.
        if (
          SOUND_NAME.win_line &&
          data.lineId !== undefined &&
          data.lineId !== null
        ) {
          soundManager.play(SOUND_NAME.win_line);
        }

        // Find a target reel for positioning (prefer middle, then second reel)
        let targetReelIndex = null;

        for (let i = 0; i < data.positions.length; i++) {
          const [reelIndex] = data.positions[i];
          if (reelIndex === GAME_LAYOUT.MIDDLE_REEL) {
            targetReelIndex = GAME_LAYOUT.MIDDLE_REEL;
            break;
          }
        }

        if (targetReelIndex === null) {
          for (let i = 0; i < data.positions.length; i++) {
            const [reelIndex] = data.positions[i];
            if (reelIndex === GAME_LAYOUT.SECOND_REEL) {
              targetReelIndex = GAME_LAYOUT.SECOND_REEL;
              break;
            }
          }
        }

        // Sync win animations and hide textures for current winning positions
        // Pre-compute line display window for per-symbol loop calculation
        const lineLoopsConfig = winLoops(
          useFastWinRef.current,
          FEATURES_CONFIG.WIN_ANIMATION_TIMING.LOOPS_PER_LINE,
        );
        const lineMasterMs = SpineAnimationData.getMasterWinDuration();
        const lineFastMs = SpineAnimationData.getFastWinDuration();
        const lineAnimType = useFastWinRef.current
          ? (FEATURES_CONFIG.WIN_ANIMATION_TIMING.WIN_FAST?.ANIMATION_NAME || "win_fast")
          : "win";
        const lineDisplayWindowMs = ((useFastWinRef.current && lineFastMs !== null) ? lineFastMs : lineMasterMs) * lineLoopsConfig;

        // ── Retire the previous text with its `out`, not a hard kill ──
        //
        // hide() is a HARD kill: alpha 0.001 on the spine, the bitmap and the
        // container, plus clearTracks(). Nothing fades — the text snaps out of
        // existence. That is why the `out` animation was never seen: every one
        // of the win controller's call sites used hide(), and playOut() — which
        // WinText.jsx exposes on the ref for exactly this — was dead code.
        //
        // playOut() sets `out` on the track and resolves on its own `complete`,
        // then _finalizeOut() does the same teardown hide() would have. Not
        // awaited on purpose: the outgoing text fades while the incoming one
        // plays its `in`, which is a crossfade rather than a gap.
        //
        // hide() is still right where a spin CANCELS a win — resetAllEffects and
        // snapClearAll — because there the text must be gone on the same frame.
        if (lineWinTextRef?.current) {
          lineWinTextRef.current.playOut();
        }
        if (globalLineWinTextRef?.current) {
          globalLineWinTextRef.current.playOut();
        }

        // Show win amount for this line (skip zero-win entries like synthetic coin triggers)
        if (data.win && lineWinTextRef?.current) {
          // ── Fit in -> idle -> out into THIS line's window ──
          //
          // Always, not just on a fast win. targetDuration only ever speeds the
          // chain UP (see _getAnimationChainDuration), so a chain shorter than
          // the line still plays at native speed and finishes its own `out`.
          // A chain LONGER than the line is compressed to fit, which is what
          // was missing: the next line opens by hard-killing this text with
          // hide(), so an overlong chain never reached `out` at all — the text
          // simply vanished.
          //
          // It used to be gated on WIN_FAST.WIN_TEXT_SPEED_UP, so disabling
          // WIN_FAST left the chain permanently unscaled.
          lineWinTextRef.current.showWinAmount(data.win, {
            targetDuration: lineDisplayWindowMs,
          });
        }

        const currentLinePositions = data.positions.map(
          ([reelIndex, symbolIndex]) => {
            const reelController =
              reelsetControllerRef.current.reelControllers[reelIndex];
            const symbol =
              reelController?.symbolsContainer?.children?.[symbolIndex];
            const symbolId = symbol?.currentSymbolId || symbol?.symbolId;
            return [reelIndex, symbolIndex, symbolId];
          },
        );

        // Pre-compute winning position Set for O(1) lookups
        const winPosSet = new Set(
          currentLinePositions.map(([r, s]) => `${r}_${s}`),
        );
        const reelsInCurrentLine = new Set(
          currentLinePositions.map(([reelIndex]) => reelIndex),
        );

        reelsetControllerRef.current.executeOnOverlayReelControllers(
          (overlayReelController) => {
            if (!overlayReelController?.isExpandingWildVisible) {
              overlayReelController?.hideAllAnimationsExcept(
                currentLinePositions,
              );
            }
          },
        );

        if (
          FEATURES_CONFIG.EXPANDING_WILDS.ENABLED &&
          FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel" &&
          expandingWildReelIndicesRef.current.length > 0
        ) {
          const reelsArray = Array.from(reelsInCurrentLine).sort(
            (a, b) => a - b,
          );
          let rightmostConnected = -1;
          for (let i = 0; i < reelsArray.length; i++) {
            const currentReel = reelsArray[i];

            if (i === 0 && currentReel > 1) {
              break;
            }

            if (i > 0 && currentReel !== reelsArray[i - 1] + 1) {
              break;
            }

            rightmostConnected = currentReel;
          }

          expandingWildReelIndicesRef.current.forEach((wildReelIndex) => {
            const overlayReelRef = overlayReelRefs.current?.[wildReelIndex];
            const wildReelHasWinningPosition =
              reelsInCurrentLine.has(wildReelIndex);
            const shouldBeBright =
              wildReelHasWinningPosition && rightmostConnected >= wildReelIndex;
            const currentlyBright =
              expandingWildAnimationStateRef.current[wildReelIndex] ?? true;

            if (shouldBeBright && !currentlyBright) {
              overlayReelRef?.playExpandingWildWinAnimation?.();
              expandingWildAnimationStateRef.current[wildReelIndex] = true;
            } else if (!shouldBeBright && currentlyBright) {
              overlayReelRef?.playExpandingWildDarkAnimation?.();
              expandingWildAnimationStateRef.current[wildReelIndex] = false;
            }
          });
        }

        if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
          winLinesContainerRef.current.hideWinLine();
        }

        if (FEATURES_CONFIG.WIN_BOX.ENABLED && winBoxContainerRef.current) {
          winBoxContainerRef.current.hideWinBoxes();
        }

        // Diff-based texture transitions — only mutate symbols that changed between lines
        const prevSet = prevWinPosSetRef.current;
        const reelIndices = GAME_LAYOUT.REEL_INDICES;

        if (prevSet) {
          // Show textures for positions that WERE winning but no longer are
          for (const prevKey of prevSet) {
            if (!winPosSet.has(prevKey)) {
              const [reel, symbolIndex] = prevKey.split("_").map(Number);
              const reelController =
                reelsetControllerRef.current.reelControllers[reel];
              if (reelController?.showSymbolTexture) {
                reelController.showSymbolTexture(symbolIndex);
              }
            }
          }
        } else {
          // First line — show all non-winning textures (full reset)
          for (let ri = 0; ri < reelIndices.length; ri++) {
            const reel = reelIndices[ri];
            const reelController =
              reelsetControllerRef.current.reelControllers[reel];
            if (!reelController) continue;

            for (
              let symbolIndex = 0;
              symbolIndex < GAME_LAYOUT.SYMBOLS_VISIBLE;
              symbolIndex++
            ) {
              if (!winPosSet.has(`${reel}_${symbolIndex}`)) {
                if (reelController.showSymbolTexture) {
                  reelController.showSymbolTexture(symbolIndex);
                }
              }
            }
          }
        }

        // ── The bonus trigger holds its celebration back ──
        //
        // A line carrying a reveal symbol plays NO line celebration while the
        // reveal runs. The gold scatter opens and spins its strip, and the full
        // line — sounds, boxes, the trigger — fires only when the strip lands;
        // see the revealPromise block below.
        //
        // ── ...and the PLAIN scatters GLOW in place while it spins ──
        //
        // They looped `win` for the whole spin (2026-09-24): in focus, but
        // bouncing, and a blank then had to cut that loop wherever it was.
        // Vladimir, 2026-09-25: *"i want them to glow, but not move... if we
        // win the free spins actually, we then dim everything and play the win
        // animation, and if not, they just stop."* `sc` has no such clip, so
        // `glow` was cut from its own `win` at the source — the ring and the
        // flash, none of the bones that move the symbol. Looped on the overlay
        // it keeps them above the dim. When the strip lands they finish the
        // pass they are in (waitForLoopSeam) before anything changes them.
        //
        // Only while the reveal is still to come. Once it has finished, later
        // passes of the cycle animate normally — otherwise the line would go
        // silent for the rest of the display.
        // Released only once the reveal has finished AND a package was really
        // awarded. A BLANK keeps this true for the rest of the display, and
        // that is what suppresses its win animations completely — it awarded
        // nothing, so there is nothing to celebrate.
        const revealSettledAndPaid =
          revealDoneForRoundRef.current ===
            (currentRoundRef.current?.id ?? null) &&
          revealAwardedForRoundRef.current ===
            (currentRoundRef.current?.id ?? null);
        const lineHasReveal =
          !revealSettledAndPaid &&
          (data.positions || []).some(([rIdx, sIdx]) => {
            const ctrl =
              reelsetControllerRef.current?.overlayReelControllers?.[rIdx];
            const sid = currentRoundRef.current?.reels?.[rIdx]?.[sIdx];
            return ctrl?.hasRevealSequence?.(sid);
          });

        // ── A BLANK NEVER CELEBRATES ──
        //
        // Said once, here, instead of by dressing up the reveal test. The
        // scatters of a trigger that AWARDED are celebrated by the ordinary
        // line presentation below — for a feature-dropped SC1 that is the only
        // thing that celebrates them, because the cycle's own reveal path was
        // already claimed by the Golden Mystery block.
        //
        // A blank awarded nothing, so nothing on that line is a win. The doors
        // still opened; there is simply nothing to cheer.
        //
        // Read from the ROUND — `gambleScatter` is the reveal payload and a
        // blank is value 0 and tier 0 — because the cycle's own refs are never
        // set when the feature opened the doors. That is the trap this file
        // fell into twice today.
        const linePackage = currentRoundRef.current?.gambleScatter;
        const linePackageBlank =
          !!linePackage &&
          (linePackage.value ?? 0) <= 0 &&
          (linePackage.tier ?? 0) <= 0;

        const positions = data.positions;
        for (let i = 0; i < positions.length; i++) {
          const [reelIndex, symbolIndex] = positions[i];
          const overlayReelController =
            reelsetControllerRef.current.overlayReelControllers[reelIndex];
          const reelController =
            reelsetControllerRef.current.reelControllers[reelIndex];

          if (!overlayReelController || !reelController) continue;

          const hasExpandingWildFullReel =
            FEATURES_CONFIG.EXPANDING_WILDS.ENABLED &&
            FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel" &&
            overlayReelController.isExpandingWildVisible === true;

          if (hasExpandingWildFullReel) {
            continue;
          }

          // Reveal line: no win yet. The reveal cell still hides its reel
          // texture, because its overlay spine is about to take the cell over.
          // The others keep their texture — hiding it with no animation to
          // replace it is what left them blank.
          if (lineHasReveal) {
            const sid = currentRoundRef.current?.reels?.[reelIndex]?.[symbolIndex];
            const revealSettled =
              revealDoneForRoundRef.current ===
              (currentRoundRef.current?.id ?? null);
            if (overlayReelController.hasRevealSequence?.(sid)) {
              // Only while its overlay is about to take the cell. After a BLANK
              // the reveal is handed back and the flat window IS the cell — see
              // the revealAwarded branch — so hiding it here on a later pass of
              // this line would leave the cell empty.
              if (!revealSettled) reelController.hideSymbolTexture?.(symbolIndex);
            } else if (sid === SCATTER_1_SYMBOL_ID && !revealSettled) {
              // Glowing in place — see the note on lineHasReveal. Only a
              // board-landed plain scatter: one the Golden Mystery dropped is
              // inside a golden window and keeps that cell's own presentation.
              reelController.hideSymbolTexture?.(symbolIndex);
              overlayReelController.syncWinAnimation(
                symbolIndex,
                sid,
                "glow",
                null,
                0,
              );
            }
            continue;
          }

          // A blank's scatters are not a win — see linePackageBlank. Only the
          // ANIMATION is skipped: the line itself carries on, because the
          // reveal block further down is what opens a board-landed scatter's
          // doors and it must still run.
          if (data.isScatter && linePackageBlank) {
            continue;
          }

          // CRITICAL: Read from spinningSprites (which has wild replacements)
          const VISIBLE_SYMBOL_START_INDEX = 1 + GAME_LAYOUT.SYMBOLS_VISIBLE;
          const spriteIndex = VISIBLE_SYMBOL_START_INDEX + symbolIndex;
          const sprite = reelController.spinningSprites?.[spriteIndex];

          if (sprite) {
            const actualSymbolId = sprite.currentSymbolId;
            if (actualSymbolId !== undefined) {
              // Per-symbol loop count: how many times this symbol's animation fits in the line display window
              const symbolWinMs = SpineAnimationData.getSymbolWinDuration(actualSymbolId, useFastWinRef.current);
              const perSymbolLoops = (symbolWinMs && symbolWinMs > 0 && lineDisplayWindowMs > 0)
                ? Math.max(1, Math.round(lineDisplayWindowMs / symbolWinMs))
                : 1;

              // Feature trigger lines (scatter/coin) loop INDEFINITELY until
              // the feature takes the screen — finite loops cause
              // play→stop→restart jitter, and leave the board dead while the
              // player is still deciding on the gamble screen.
              const isFeatureTriggerLine =
                (data.isScatter && store.getState().freeSpins.triggered) ||
                (data.isCoin && (
                  store.getState().featureGame.royalCollectTriggered ||
                  store.getState().potCollection.potCollectionTriggered ||
                  selectHoldAndWinIntroPending(store.getState())
                ));
              const effectiveLoops = isFeatureTriggerLine ? 0 : perSymbolLoops;

              overlayReelController.syncWinAnimation(
                symbolIndex,
                actualSymbolId,
                lineAnimType,
                null,
                effectiveLoops,
              );
            }
          }

          if (reelController.hideSymbolTexture) {
            reelController.hideSymbolTexture(symbolIndex);
          }
        }

        // Store current positions for next line's diff
        prevWinPosSetRef.current = winPosSet;

        if (
          FEATURES_CONFIG.WIN_LINES.ENABLED &&
          winLinesContainerRef.current &&
          data.lineId !== undefined &&
          data.lineId !== null
        ) {
          winLinesContainerRef.current.showWinLine(data.lineId);
        }

        // Show win boxes — either on winning positions only or full line path
        if (
          FEATURES_CONFIG.WIN_BOX.ENABLED &&
          winBoxContainerRef.current &&
          data.line
        ) {
          const winBoxPositions = FEATURES_CONFIG.WIN_BOX.WINNING_ONLY
            ? data.positions // Only matched/winning symbol positions
            : data.line.map((row, reelIndex) => [reelIndex, row]); // Full line path

          // Compute target loops from pre-cached durations
          let winBoxTargetLoops = 0;
          // The line's window — the box plays its loops in exactly this.
          let winBoxWindowMs = 0;
          const winBoxAnimMs = SpineAnimationData.getWinBoxDuration();
          if (FEATURES_CONFIG.WIN_BOX.SNAP_TO_LOOP && winBoxAnimMs > 0) {
            const fastWinDur = SpineAnimationData.getFastWinDuration();
            const baseDur = (useFastWinRef.current && fastWinDur !== null)
              ? fastWinDur
              : SpineAnimationData.getMasterWinDuration();
            const lineLoops = winLoops(
              useFastWinRef.current,
              FEATURES_CONFIG.WIN_ANIMATION_TIMING.LOOPS_PER_LINE,
            );
            // winBoxTargetLoops = Math.max(1, Math.floor((baseDur * lineLoops) / winBoxAnimMs));
            // Rounded: 2000 / 666.7 = 2.9998 is 3 loops, filling the line.
            winBoxTargetLoops = Math.max(1, Math.round((baseDur * lineLoops) / winBoxAnimMs));
            winBoxWindowMs = baseDur * lineLoops;
          }

          // winBoxContainerRef.current.showWinBoxes(winBoxPositions, winBoxTargetLoops);
          winBoxContainerRef.current.showWinBoxes(winBoxPositions, winBoxTargetLoops, winBoxWindowMs);
        }

        // ── REVEAL SYMBOLS: started WITH the win, not after it ──
        //
        // Launched with the line, not after it. Waiting for the other symbols
        // to finish their win before opening this one put a dead beat in the
        // middle of the trigger. It is awaited after the line, so the round is
        // still held until the sequence settles — it just no longer starts
        // late.
        //
        // Keyed off the SYMBOL having a reveal sequence, not off the win type.
        // This is a bonus trigger; whether the win that carried it was a
        // scatter, a coin or a payline is irrelevant.
        //
        // ONCE PER ROUND. This sits inside the win-line cycling loop, which
        // repeats for as long as the win display runs.
        // Assigned by the free-spins block below, and called either on the
        // line's own timer or — when a reveal is running — only after the
        // reveal and its celebration have finished. Declared out here so both
        // paths can reach it.
        let openFeatureScreen = null;

        let revealPromise = null;
        const revealRoundId = currentRoundRef.current?.id ?? null;
        if (revealPlayedForRoundRef.current !== revealRoundId) {
          const revealTargets = [];
          // ── The cell the GAMBLE SCATTER is on, wherever it came from ──
          //
          // A gamble scatter dropped by the Golden Mystery is not on the board:
          // the screen holds B2 at that coord, because the cell is a golden
          // window with the scatter standing inside it. Read off the board, the
          // symbol there is 10, which has no reveal sequence — so the doors
          // never opened and the third scatter of round 1451830 just sat there.
          //
          // The round already states where the scatter is (the RGS builds
          // `gambleScatter` from either source), so that position is the
          // authority for WHICH cell reveals, and the board symbol is only
          // consulted for the others.
          const revealScatter = currentRoundRef.current?.gambleScatter;
          const revealScatterKey = Array.isArray(revealScatter?.position)
            ? `${revealScatter.position[0]},${revealScatter.position[1]}`
            : null;

          for (const [rIdx, sIdx] of data.positions || []) {
            const ctrl =
              reelsetControllerRef.current?.overlayReelControllers?.[rIdx];
            const boardSymbol = currentRoundRef.current?.reels?.[rIdx]?.[sIdx];
            // SC1's own cell reveals AS SC1 even when the board disagrees. The
            // golden lid underneath is left exactly where it is — this opens on
            // top of it, which is the layer the feature was missing.
            const sid =
              revealScatterKey && `${rIdx},${sIdx}` === revealScatterKey
                ? SCATTER_2_SYMBOL_ID
                : boardSymbol;
            if (ctrl?.hasRevealSequence?.(sid)) {
              revealTargets.push({ ctrl, sIdx, sid, rIdx });
            }
          }
          // Says whether the doors OPEN, and for which round id. The guard
          // above is once-per-round-id, and a replay repeats one id — so on a
          // second playthrough this is the line that proves the re-arm worked.
          baseDiag("winController -> scatter reveal", {
            roundId: revealRoundId,
            targets: revealTargets.length,
            cells: revealTargets.map((t) => `${t.rIdx},${t.sIdx}:${t.sid}`),
          });
          if (revealTargets.length > 0) {
            // ── Claim the round only when the doors ACTUALLY open ──
            //
            // This used to be claimed at the top of the block, before the
            // targets were collected. A pass that found NO targets — the
            // overlay controllers not mounted yet, which a round carrying real
            // line wins reaches on a different pass than a trigger-only one —
            // still took the round id, and every later pass then returned at
            // the guard above. The scatters sat on a dimmed board and the doors
            // never opened.
            //
            // The same shape as the replay fault described on the ref itself,
            // one round instead of two. Claiming HERE means a fruitless pass
            // costs nothing and the next one retries; the claim and the launch
            // are in one synchronous block, so it still cannot open twice.
            revealPlayedForRoundRef.current = revealRoundId;
            dispatchRef.current(setCriticalAnimationPlaying(true));

            // ── THE SCENE ANSWERS THE GOLDEN SCATTER ──
            //
            // Damir, after demo 2: the golden reveal has no anticipation around
            // it. The scatter itself already glows — `loop: ["anticipation"]`
            // runs on `scg` for the whole strip spin — but that is inside the
            // cell, and the ROOM says nothing while the game decides how many
            // free spins the player gets.
            //
            // So the reels light, exactly as they do for a Golden Mystery: same
            // clip (`reel_highlight`), same dedicated REELS track, held from
            // here until the strip settles. See litSceneForGolden above — this
            // is that moment's twin, and the two can never collide because a
            // round has either a mystery reveal or a scatter reveal, not both.
            //
            // The REELS track ONLY. The lamp lives on MAIN, which the win
            // celebration also uses, and this reveal ends straight into the
            // trigger's celebration — taking MAIN would mean handing it back a
            // frame later. The reel glow is what was asked for and it is the
            // one that reads as tension around the board.
            //
            // No reel slowdown and no speed change: this game has none, and the
            // glow is the whole effect.
            gameViewControllerRef?.current?.playAccent(
              SCENE_ACCENTS.MYSTERY_WAIT_STRONG,
              { loop: true, track: SCENE_ACCENT_TRACKS.REELS },
            );

            revealPromise = Promise.all(
              revealTargets.map(({ ctrl, sIdx, sid, rIdx }) => {
                const gambleScatter = currentRoundRef.current?.gambleScatter;
                return ctrl
                  .playRevealSequence(sIdx, sid, gambleScatter)
                  .then(() => {
                    // ── Keep the texture UNDER the overlay honest ──
                    //
                    // The reveal only changed what the overlay shows. The reel
                    // still holds the symbol that landed, so resetAllEffects —
                    // which hides the overlay and then calls
                    // showAllSymbolTextures — resurfaces it on the next spin.
                    // For a blank that means a closed gold door where the
                    // player last saw an empty window.
                    //
                    // TEXTURE ONLY — never the cell's identity. The wild's
                    // replaceSymbolAtPosition would also rewrite
                    // currentSymbolId, and the win cycle reads that field to
                    // pick the celebrating spine; pointing it at a
                    // texture-only symbol stopped the scatter animating and
                    // desynced it from the other two. This cell is still the
                    // gamble scatter, so all three keep celebrating together
                    // on `scg` while only the hidden picture becomes blank.
                    const cell = ctrl.getRevealStaticCell?.(
                      sid,
                      gambleScatter,
                    );
                    if (!cell) return null;
                    reelsetControllerRef.current?.reelControllers?.[
                      rIdx
                    ]?.setSymbolTextureOnly?.(
                      sIdx,
                      cell.symbolId,
                      cell.texture,
                    );
                    // Handed back so the caller can see what was landed —
                    // this is the award signal, straight off the round.
                    return cell;
                  });
              }),
            );
          }
        }

        // Hoisted: the reveal block below opens the feature screen on this
        // same duration, and it is assigned inside the promise.
        let lineDuration = 0;

        await new Promise((resolve) => {
          // Duration from SpineAnimationData (extracted once at init)
          const targetLoops = winLoops(
            useFastWinRef.current,
            FEATURES_CONFIG.WIN_ANIMATION_TIMING.LOOPS_PER_LINE,
          );
          const masterDuration = SpineAnimationData.getMasterWinDuration();
          const fastWinDuration = SpineAnimationData.getFastWinDuration();
          const baseDuration = (useFastWinRef.current && fastWinDuration !== null)
            ? fastWinDuration
            : masterDuration;
          lineDuration = baseDuration * targetLoops;

          // Snap to complete win box loops to prevent partial-loop jitter
          // ── OFF (Vladimir, 2026-09-30: "one line is cut") ── it floored the
          // line to whole box loops: 2000 / 666.7 = 2.9998 -> 2 -> 1333 ms, so
          // the line switched mid-way through the symbols' win. The line follows
          // the symbols' animation; the box's loop count fits it (showWinBoxes).
          // const winBoxAnimMs = SpineAnimationData.getWinBoxDuration();
          // if (
            // FEATURES_CONFIG.WIN_BOX.ENABLED &&
            // FEATURES_CONFIG.WIN_BOX.SNAP_TO_LOOP &&
            // winBoxAnimMs > 0
          // ) {
            // const completeLoops = Math.floor(lineDuration / winBoxAnimMs);
            // if (completeLoops >= 1) {
              // lineDuration = completeLoops * winBoxAnimMs;
            // }
          // }

          // Play win_line a second time at half the line duration
          if (
            FEATURES_CONFIG.WIN_LINES.ENABLED &&
            SOUND_NAME.win_line &&
            data.lineId !== undefined &&
            data.lineId !== null
          ) {
            const secondPlayTimeout = new TickerTimeout(() => {
              soundManager.play(SOUND_NAME.win_line);
            }, lineDuration / 2);
            currentTimeoutsRef.current.push(secondPlayTimeout);
          }

          // ── FREE SPIN TRIGGER: the gamble screen, not the intro ──
          //
          // GDD §9: the player gambles the PACKAGE before playing it, so the
          // gamble screen comes first and the intro follows once the package
          // is settled. The one exception is a tier-5 (gold) award, which the
          // GDD sends straight to free spins with no gamble offered — the
          // ladder has nothing above it to win.
          //
          // Everything below this point — the wait-for-animated-win poll and
          // the delay — is Zeus's, unchanged. Only the popup differs.
          if (
            FEATURES_CONFIG.FREE_SPINS.ENABLED &&
            data.isScatter &&
            store.getState().freeSpins.triggered
          ) {
            // The gamble screen opens ONLY while there is a question to ask:
            // the ladder unanswered (`gambleSettled` false) AND outcomes left
            // to win (`variants` non-empty — the player who pressed SKIP has
            // answered, and the server marks it by emptying the pool).
            // Otherwise the win replay hands straight to the intro: forcing
            // the gamble screen on a settled package showed a screen with no
            // data to offer. Decided at CALL time, off the slice — the one
            // place both live play and every reload keep current.
            openFeatureScreen = () => {
              baseDiag("winController -> opening feature screen");
              const fs = store.getState().freeSpins;
              const canGamble =
                !fs.gambleSettled && (fs.gamble?.variants?.length ?? 0) > 0;
              dispatchRef.current(
                canGamble ? setShowGambleDialog(true) : setShowFreeSpinIntro(true),
              );
            };

            // ── When a reveal is running, DO NOT schedule on the line timer ──
            //
            // The line lasts about as long as one win animation; the reveal
            // takes far longer — doors 0.5s, strip 2.6s, then the stop. On the
            // line timer the gamble screen opened on top of a strip that was
            // still spinning. The revealPromise block below opens it instead,
            // once the strip has landed AND the three win loops have played.
            //
            // Nothing is scheduled here when a reveal is running. Everything
            // below is Zeus's original timing, and it only applies to a
            // trigger with no reveal to wait for.
            if (!revealPromise) {
              // If animated win is still showing (4-5 scatters), wait for it
              // to close first.
              const animState = store.getState();
              if (selectWinAnimationsPlaying(animState)) {
                const waitForAnimatedWin = new Promise((resolve) => {
                  const poll = () => {
                    const currentState = store.getState();
                    if (!selectWinAnimationsPlaying(currentState)) {
                      resolve();
                    } else {
                      const t = new TickerTimeout(poll, 100);
                      currentTimeoutsRef.current.push(t);
                    }
                  };
                  poll();
                });
                waitForAnimatedWin.then(openFeatureScreen);
              } else {
                const delayTimeout = new TickerTimeout(() => {
                  const idx = currentTimeoutsRef.current.indexOf(delayTimeout);
                  if (idx > -1) currentTimeoutsRef.current.splice(idx, 1);
                  openFeatureScreen();
                }, lineDuration / 1.25);
                currentTimeoutsRef.current.push(delayTimeout);
              }
            }
          }

          // ROYAL COLLECT TRIGGER: Show intro after trigger symbol animation finishes
          if (
            FEATURES_CONFIG.ROYAL_COLLECT.ENABLED &&
            data.isCoin &&
            store.getState().featureGame.royalCollectTriggered
          ) {
            const animState = store.getState();
            if (selectWinAnimationsPlaying(animState)) {
              const waitForAnimatedWin = new Promise((resolve) => {
                const poll = () => {
                  const currentState = store.getState();
                  if (!selectWinAnimationsPlaying(currentState)) {
                    resolve();
                  } else {
                    const t = new TickerTimeout(poll, 100);
                    currentTimeoutsRef.current.push(t);
                  }
                };
                poll();
              });
              waitForAnimatedWin.then(() => {
                dispatchRef.current(setShowRoyalCollectIntro(true));
              });
            } else {
              const delayTimeout = new TickerTimeout(() => {
                const idx = currentTimeoutsRef.current.indexOf(delayTimeout);
                if (idx > -1) currentTimeoutsRef.current.splice(idx, 1);
                dispatchRef.current(setShowRoyalCollectIntro(true));
              }, lineDuration);
              currentTimeoutsRef.current.push(delayTimeout);
            }
          }

          // POT COLLECTION TRIGGER: Show intro after trigger symbol animation finishes
          // Skip for mid-bonus reload — we go straight to bonus scene
          if (
            FEATURES_CONFIG.POT_COLLECTION.ENABLED &&
            data.isCoin &&
            store.getState().potCollection.potCollectionTriggered &&
            store.getState().potCollection.reloadPhase !== "respins"
          ) {

            // Dispatch PC intro flag immediately. PotCollectionIntroDialog gates
            // its startSequence on !infoPopupActive so if FR info popup is still
            // open, the intro waits for it to close naturally via React re-render.
            const showPCIntroWhenReady = () => {

              dispatchRef.current(setShowPotCollectionIntro(true));
            };

            const animState = store.getState();
            if (selectWinAnimationsPlaying(animState)) {

              const waitForAnimatedWin = new Promise((resolve) => {
                const poll = () => {
                  const currentState = store.getState();
                  if (!selectWinAnimationsPlaying(currentState)) {
                    resolve();
                  } else {
                    const t = new TickerTimeout(poll, 100);
                    currentTimeoutsRef.current.push(t);
                  }
                };
                poll();
              });
              waitForAnimatedWin.then(() => {

                showPCIntroWhenReady();
              });
            } else {

              const delayTimeout = new TickerTimeout(() => {
                const idx = currentTimeoutsRef.current.indexOf(delayTimeout);
                if (idx > -1) currentTimeoutsRef.current.splice(idx, 1);
                showPCIntroWhenReady();
              }, lineDuration);
              currentTimeoutsRef.current.push(delayTimeout);
            }
          }

          // HOLD & WIN TRIGGER: Show intro after the trigger coins' line
          // finishes — the Pot Collection opener, on the holdAndWin slice.
          // Not on a mid-feature reload: the room is already on screen.
          if (
            data.isCoin &&
            selectHoldAndWinIntroPending(store.getState()) &&
            store.getState().holdAndWin.reloadPhase !== "in_progress"
          ) {
            const showHWIntro = () => {
              dispatchRef.current(setShowHoldAndWinIntro(true));
            };

            if (selectWinAnimationsPlaying(store.getState())) {
              const waitForAnimatedWin = new Promise((resolve) => {
                const poll = () => {
                  if (!selectWinAnimationsPlaying(store.getState())) {
                    resolve();
                  } else {
                    const t = new TickerTimeout(poll, 100);
                    currentTimeoutsRef.current.push(t);
                  }
                };
                poll();
              });
              waitForAnimatedWin.then(showHWIntro);
            } else {
              const delayTimeout = new TickerTimeout(() => {
                const idx = currentTimeoutsRef.current.indexOf(delayTimeout);
                if (idx > -1) currentTimeoutsRef.current.splice(idx, 1);
                showHWIntro();
              }, lineDuration);
              currentTimeoutsRef.current.push(delayTimeout);
            }
          }

          const timeoutId = new TickerTimeout(() => {
            const idx = currentTimeoutsRef.current.indexOf(timeoutId);
            if (idx > -1) currentTimeoutsRef.current.splice(idx, 1);
            resolve();
          }, lineDuration);
          currentTimeoutsRef.current.push(timeoutId);
        });

        // The reveal was launched alongside the win above. Hold the round
        // here until it settles — the scatters' win has already finished by
        // now, the reveal is simply longer.
        if (revealPromise) {
          try {
            const revealCells = await revealPromise;
            revealDoneForRoundRef.current = currentRoundRef.current?.id ?? null;


            // ── Only a WON package earns a presentation ──
            //
            // Up to this point the board has stayed clear: three scatters
            // landed and bounced, the UI was locked, and the strip spun with
            // no dim and no win animations. What happens next depends entirely
            // on what it landed on.
            //
            // Read from the ROUND, via the reveal config's own `awarded`. The
            // outcome has been on the round as `gambleScatter` since the spin
            // resolved, so there is no reason to ask the slice — and asking it
            // meant this path could only ever run on a live server round.
            //
            // A blank falls straight through: nothing celebrates, the screen
            // never dims, and the `finally` below hands the UI back.
            const revealAwarded = (revealCells || []).some((c) => c?.awarded);

            // ── The glow finishes its pass before anything changes it ──
            //
            // Whichever way the strip landed, the plain scatters are mid-glow.
            // Both branches below change them — a blank hands them back to
            // their texture, an award starts their `win` — and either one done
            // on this frame cuts a lit ring dead. So the pass they are in runs
            // out to its seam first; the clip is authored to be back at rest
            // there. Their own animation decides the wait, not a timer.
            // Vladimir, 2026-09-25: *"WE NEED TO LET SYMBOLS FINISH WE DONT USE
            // TIME, we use their animation so it flows naturally."*
            await Promise.all(
              (data.positions || []).map(([rIdx, sIdx]) => {
                const sid = currentRoundRef.current?.reels?.[rIdx]?.[sIdx];
                if (sid !== SCATTER_1_SYMBOL_ID) return null;
                return reelsetControllerRef.current?.overlayReelControllers?.[
                  rIdx
                ]?.waitForLoopSeam?.(sIdx);
              }),
            );

            // ── The blank's own sound, the counterpart to scatter_win ──
            //
            // The third scatter is the gold one, and the strip inside it has
            // just stopped. If it stopped on a gamble level the round wins its
            // free spins and `scatter_win` fires below. If it stopped on a
            // BLANK nothing else happens at all — the screen never dims, no
            // line celebrates, and the `finally` hands the UI back. That
            // silence was the whole outcome being delivered without a sound.
            //
            // Fired here rather than in an `else` so the two sit on the same
            // decision, one line apart, and neither can drift from it.
            //
            // Levelled ONTO scatter_win, not near it: both are the same beat
            // answered two ways, so a player must hear which answer came up,
            // not which was louder. See SOUND_VOLUMES.
            if (!revealAwarded) {
              soundManager.play(SOUND_NAME.scatter_lose);

              // ── A blank ends the focus — all three go back to textures ──
              //
              // The gold one is still its live reveal spine, settled on its own
              // loop seam and holding the empty window. It sits on the overlay,
              // ABOVE the dim, so when the cycle went back to the line wins the
              // losing window stayed lit like a winner. The plain scatters have
              // just finished a glow pass (see above), so they are at rest and
              // go back to their texture without a jump.
              // Vladimir, 2026-09-24: *"that empty scatter is a losing one, we
              // need to have texture of it that will be dimmed."*
              //
              // The window's flat picture is already on the reel underneath —
              // the reveal's `.then` painted it from getRevealStaticCell — so
              // the reveal is simply handed back and that texture uncovered.
              // releaseRevealSymbol is the overlay's own hand-back: it destroys
              // the reveal controller and parks the spine asleep.
              for (const [rIdx, sIdx] of data.positions || []) {
                const sid = currentRoundRef.current?.reels?.[rIdx]?.[sIdx];
                const ctrl =
                  reelsetControllerRef.current?.overlayReelControllers?.[rIdx];
                const rc = reelsetControllerRef.current?.reelControllers?.[rIdx];
                if (!ctrl || !rc) continue;
                if (ctrl.hasRevealSequence?.(sid)) {
                  ctrl.releaseRevealSymbol?.(sIdx, sid);
                  rc.showSymbolTexture?.(sIdx);
                } else if (sid === SCATTER_1_SYMBOL_ID) {
                  ctrl.hideWinAnimation?.(sIdx);
                  rc.showSymbolTexture?.(sIdx);
                }
              }
            }
            if (revealAwarded) {
              // Recorded so the earlier suppression gate can release: without
              // it, later passes of the win cycle would keep the trigger line
              // silent for a round that DID pay.
              revealAwardedForRoundRef.current =
                currentRoundRef.current?.id ?? null;
              // Dim NOW — not when the scatters landed. This is the moment
              // there is finally a result worth presenting.
              dimReelSetRef.current?.dimReels(true);

              // ── And the scene reacts, on the same beat ──
              //
              // THE moment the bonus is won: the strip has stopped, it did not
              // land blank, and the three scatters are about to celebrate. The
              // rat goes with them.
              //
              // It used to fire when the scatters LANDED, off
              // `freeSpins.triggered`, which was before any of that was true —
              // the strip was still spinning and could still land on a blank.
              // A blank now falls through this whole branch, so the scene stays
              // quiet for a trigger that awarded nothing, which is the point.
              //
              // Only when the round PAID NOTHING. A paying trigger already has
              // the rat looping from the win presentation above, and re-firing
              // would restart the clip from frame 0 mid-zap for no reason. The
              // two sites split on `win` and so can never both fire.
              //
              // HELD, like every other win reaction. The window is short by
              // design — the feature screen opens a beat later — and the room
              // taking the screen is one of the two stopAccent sites, so it is
              // released rather than left ticking behind a hidden view.
              if ((currentRoundRef.current?.win ?? 0) === 0) {
                gameViewControllerRef?.current?.playAccent(SCENE_ACCENTS.WIN, {
                  loop: true,
                });
              }

              // ── The trigger's own sound ──
              //
              // The doors are open, the package landed and PAID, and the
              // animations below are the actual win presentation. This is
              // where it belongs.
              //
              // NOT gated on `hadDeferredWinSound`. That flag only says a
              // basic chime was owed, which is an artefact of the basic_win
              // plumbing — it is false whenever the round is a big/mega win,
              // and the bonus trigger is the same event whatever the round
              // paid. So the flag is still read and cleared above (a stale one
              // must never leak into the next round), and the sound fires on
              // the trigger itself.
              //
              // It REPLACES basic_win here. The chime was standing in for a
              // sound this moment did not have; it has one now.
              soundManager.play(SOUND_NAME.scatter_win);

              // ── NOW all three celebrate ──
              //
              // The strip has landed and the player can see what was won, so
              // the whole trigger line fires at once. 0 = loop for ever, which
              // is what a trigger line has always done — it runs until the
              // feature takes the screen.
              for (const [rIdx, sIdx] of data.positions || []) {
                const ctrl =
                  reelsetControllerRef.current?.overlayReelControllers?.[rIdx];
                const rc =
                  reelsetControllerRef.current?.reelControllers?.[rIdx];
                const sid = currentRoundRef.current?.reels?.[rIdx]?.[sIdx];
                if (!ctrl || sid === undefined) continue;

                if (ctrl.hasRevealSequence?.(sid)) {
                  // The scatter that opened. Its card is a slot object and
                  // survives the animation change, so it keeps showing.
                  ctrl.playRevealWin?.(sIdx);
                } else {
                  rc?.hideSymbolTexture?.(sIdx);
                  ctrl.syncWinAnimation(sIdx, sid, "win", null, 0);
                }
              }

              // ── Then the feature screen, on the ORIGINAL delay ──
              //
              // `lineDuration / 1.25` is the wait this trigger has always
              // used; the only thing that changed is WHERE it is measured
              // from. It could not stay on the line timer because the reveal
              // outlasts the line, so the screen opened over a strip still
              // spinning.
              if (openFeatureScreen) {
                const openTimeout = new TickerTimeout(() => {
                  const idx = currentTimeoutsRef.current.indexOf(openTimeout);
                  if (idx > -1) currentTimeoutsRef.current.splice(idx, 1);
                  openFeatureScreen();
                }, lineDuration / 1.25);
                currentTimeoutsRef.current.push(openTimeout);
              }
            }
          } finally {
            // ── The strip has landed, so the room stops asking ──
            //
            // Ends the reel glow lit when this reveal launched. HERE because
            // this block is inside `if (revealPromise)` — it runs only when a
            // reveal was actually started, which is exactly when the glow was
            // lit, and it runs whether the reveal settled or threw. Putting it
            // after the `await` instead would strand the glow on for ever on a
            // reveal that failed, since there is no `catch`.
            //
            // `stopSceneAccents` skips one-shots and fitted accents already
            // counting out, so this can only end something genuinely held.
            gameViewControllerRef?.current?.stopAccent();

            // ── Who owns the lock ──
            //
            // A bonus trigger holds criticalAnimationPlaying from the moment
            // the scatters land until the round actually starts, so nothing
            // underneath can be spun while the reveal, the gamble screen and
            // the intro are on top of it. Releasing it here — the instant the
            // reveal settled — handed the UI back with the gamble screen still
            // open.
            //
            // It is released in exactly two places, both of which end the
            // trigger: the wipe's midpoint when the round begins, and the
            // gamble dialog's close-after-loss when the package is forfeit.
            if (!store.getState().freeSpins.triggered) {
              dispatchRef.current(setCriticalAnimationPlaying(false));
            }
          }
        }

        // Past every skip, so this line was really presented.
        playedAny = true;

        // After the first line's animation completes, fire the callback to unblock spin
        if (index === 0 && onFirstLineComplete) {
          onFirstLineComplete();
          onFirstLineComplete = null; // Only fire once
        }


      }

      return playedAny;
    },
    [
      winLinesContainerRef,
      winBoxContainerRef,
      globalLineWinTextRef,
      overlayReelRefs,
      lineWinTextRef,
      // A context ref — a stable identity, so listing it satisfies the linter
      // without changing when this callback is rebuilt.
      gameViewControllerRef,
      // Added with the reveal's own dim — this callback now owns the dim for a
      // trigger, which it previously never touched.
      dimReelSetRef,
    ],
  );

  /**
   * Shows all winning symbols simultaneously before line cycling begins.
   * Creates a celebration effect by displaying all winning animations at once.
   */
  const showAllWinningSymbolsOnce = useCallback(
    async (
      winningData,
      hasExpandingWilds,
      expandingWildReelIndices = [],
      winType = null,
      isAnimatedWin = false,
    ) => {
      const allPositions = [];
      const lineIds = Object.keys(winningData);

      // ── A TRIGGER ENTRY IS NOT A WIN ──
      //
      // Show-all lights every winning cell at once. The trigger's own entry —
      // the synthetic scatter win, or a coin trigger — carries POSITIONS and
      // pays 0, so including it lit the scatters as though they had been paid.
      // On a Golden Mystery round that ends on a BLANK gamble scatter it was at
      // its worst: the empty window celebrated beside the coins that actually
      // paid. Vladimir, 2026-09-18: *"we didnt win that thing, so only top 2
      // coins should be winning."*
      //
      // Nothing is lost. The scatters get their own beat immediately after —
      // orderLineIds sorts the trigger entry LAST and the cycle jumps straight
      // to it — which is where they are meant to light, together, once.
      //
      // A round whose ONLY wins are trigger entries never reaches here
      // (`skipShowAllForTriggerOnly`), but the fallback keeps that honest: if
      // the filter empties the list, show everything rather than nothing.
      // `isScatter` only — NOT `isCoin`.
      //
      // The two are not the same kind of entry. A scatter trigger pays 0 and
      // exists to carry positions. `isCoin` is Pot Collection's and Royal
      // Collect's trigger line and it carries REAL money (RGS.js, the "coin"
      // tuple), so filtering it here would leave paid cells dark on those
      // games. The Golden Mystery's own payout is `isFeaturePayout` and is
      // untouched by either test.
      const payingLineIds = lineIds.filter((lineId) => {
        const data = winningData[lineId];
        return !data?.isScatter;
      });
      const showAllLineIds = payingLineIds.length ? payingLineIds : lineIds;

      showAllLineIds.forEach((lineId) => {
        const data = winningData[lineId];
        data.positions.forEach((position) => {
          const [reelIndex, symbolIndex] = position;
          const exists = allPositions.some(
            ([r, s]) => r === reelIndex && s === symbolIndex,
          );
          if (!exists) {
            allPositions.push(position);
          }
        });
      });

      // Targeted cleanup: hide landing/idle animations on NON-winning positions only.
      // Build a set of winning positions per reel so we can exclude them.
      const winPositionsByReel = new Map();
      allPositions.forEach(([reelIndex, symbolIndex]) => {
        if (!winPositionsByReel.has(reelIndex)) {
          winPositionsByReel.set(reelIndex, new Set());
        }
        winPositionsByReel.get(reelIndex).add(symbolIndex);
      });

      executeOnOverlayReelControllersRef.current(
        (overlayReelController, reelIndex) => {
          if (overlayReelController) {
            const exceptPositions = winPositionsByReel.get(reelIndex) || null;
            overlayReelController.hideAllAnimations(exceptPositions);
          }
        },
      );

      executeOnReelControllersRef.current((controller) => {
        if (controller && controller.showAllSymbolTextures) {
          controller.showAllSymbolTextures();
        }
      });

      // ── Play win sound for non-animated wins ──
      //
      // The regular symbols' win, and it plays HERE, on its own — this runs
      // before the doors open. It is no longer withheld: the doors have their
      // own sound now, so the sequence is just played in order, and the reveal
      // CUTS this chime as it opens (`cutSound` in gambleScatterConfig) rather
      // than this site trying to predict the reveal and stay silent for it.
      // Same swap as the other site — see goldenWinSound. These two are
      // mutually exclusive: a trigger-only round skips show-all entirely and
      // takes the other one, so gating just one of them is never enough.
      if (!isAnimatedWin) {
        const winChime = goldenWinSound(winningData);
        if (winChime) soundManager.play(winChime);
      }

      // === STEP 3: Determine win animation type (fast vs regular) ===
      const winFastConfig = FEATURES_CONFIG.WIN_ANIMATION_TIMING.WIN_FAST;
      const currentStateForAnim = store.getState();
      const isAutoplayActive = currentStateForAnim.settings.autoPlayActive;
      const useFastWin =
        winFastConfig?.ENABLED &&
        (spacebarTurboActiveRef.current || isAutoplayActive);
      const winAnimationType = useFastWin
        ? (winFastConfig.ANIMATION_NAME || "win_fast")
        : "win";

      // Persist fast-win decision so cycleThroughLines uses the same timing/animation
      useFastWinRef.current = useFastWin;

      // Compute display window duration for "show all wins" phase
      const masterWinMs = SpineAnimationData.getMasterWinDuration();
      const fastWinMs = SpineAnimationData.getFastWinDuration();
      // Turbo holds the global win for one loop instead of two.
      const showAllNormalLoops = isTurboLike(
        store.getState().settings.autoPlaySpeed,
      )
        ? FEATURES_CONFIG.SHOW_ALL_WINS.LOOPS_FOR_SHOW_ALL_TURBO
        : FEATURES_CONFIG.SHOW_ALL_WINS.LOOPS_FOR_SHOW_ALL;
      const showAllLoopsConfig = winLoops(useFastWin, showAllNormalLoops);
      const displayWindowMs = ((useFastWin && fastWinMs !== null) ? fastWinMs : masterWinMs) * showAllLoopsConfig;

      // ── A TRIGGER's global pass loops for as long as the big-win popup is up ──
      //
      // The popup holds `winAnimationsPlaying` until the player dismisses it,
      // and the scatter line waits for that (see cycleThroughLines). Left as
      // a fixed number of loops, the global pass ended under the popup and
      // the board sat frozen behind it. So on a trigger round with a
      // celebration the cells loop for ever here, the wait below extends to
      // the popup's close, and then runs out the CURRENT pass to its end so
      // nothing is cut — the scatter line's own clean-up stops the loops.
      // Vladimir, 2026-09-24: *"close the big win popup -> finish global
      // animation properly -> move to scatter line."*
      const holdForPopup = isAnimatedWin && isTriggerPresentationRound();
      const passStartedAt = performance.now();

      // Start spine win animations on winning symbols
      for (let i = 0; i < allPositions.length; i++) {
        const [reelIndex, symbolIndex] = allPositions[i];
        const overlayReelController =
          reelsetControllerRef.current.overlayReelControllers[reelIndex];
        const reelController =
          reelsetControllerRef.current.reelControllers[reelIndex];

        if (!overlayReelController || !reelController) {
          continue;
        }

        const hasExpandingWildFullReel =
          FEATURES_CONFIG.EXPANDING_WILDS.ENABLED &&
          FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel" &&
          overlayReelController.isExpandingWildVisible === true;

        if (hasExpandingWildFullReel) {
          continue;
        }

        const symbol = reelController.symbolsContainer.children[symbolIndex];
        if (symbol) {
          const actualSymbolId = symbol.currentSymbolId || symbol.symbolId;
          if (actualSymbolId !== undefined) {
            // Per-symbol loop count: how many times this symbol's animation fits in the display window
            const symbolWinMs = SpineAnimationData.getSymbolWinDuration(actualSymbolId, useFastWin);
            const perSymbolLoops = (symbolWinMs && symbolWinMs > 0 && displayWindowMs > 0)
              ? Math.max(1, Math.round(displayWindowMs / symbolWinMs))
              : 1;
            // ── A reveal symbol keeps its doors shut until it opens ──
            //
            // scg's `win` clip is authored for the OPENED state: it deletes
            // both door attachments and fades the gamble backing, because by
            // the time it normally plays the package is already showing
            // through. Played here — in the global show-all, before the
            // reveal has run — it opens the doors onto nothing, which is the
            // "weird" look. `win_closed` is the same bounce with the doors
            // put back. Any symbol without that clip falls back to its
            // ordinary win, so this is safe to ask for on every cell.
            const revealSettled =
              revealDoneForRoundRef.current ===
              (currentRoundRef.current?.id ?? null);
            const cellWinType =
              !revealSettled &&
              overlayReelController.hasRevealSequence?.(actualSymbolId)
                ? "win_closed"
                : winAnimationType;
            overlayReelController.showWinAnimation(
              symbolIndex,
              actualSymbolId,
              cellWinType,
              null,
              holdForPopup ? 0 : perSymbolLoops,
            );

            // ── A GOLDEN cell's win MOVES the cell, so the flat copy shows ──
            //
            // This phase deliberately does NOT hide textures: an ordinary
            // symbol's win plays in place, and its overlay spine simply covers
            // the texture underneath. A Golden cell is different — `my.win`
            // keys `myst_main`, the bone the whole cell hangs off, so frame,
            // lid and coin all travel together and the reel's identical flat
            // composite is left standing still behind them. The player sees
            // the coin twice: one bouncing, one parked.
            //
            // Only Golden cells, so nothing about an ordinary symbol changes.
            // Nothing has to put it back either — showAllSymbolTextures runs
            // at the top of this phase and again between win lines.
            if (overlayReelController.hasGoldenMystery?.(symbolIndex)) {
              reelController.hideSymbolTexture?.(symbolIndex);
            }
          }
        }
      }

      const isFullReelMode =
        FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel";
      const isCascadeMode =
        FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "cascade";

      if (!isFullReelMode && expandingWildReelIndices.length > 0) {
        expandingWildReelIndices.forEach((reelIndex) => {
          const reelController =
            reelsetControllerRef.current.reelControllers[reelIndex];
          if (reelController && reelController.showAllSymbolTextures) {
            reelController.showAllSymbolTextures();
          }
        });
      }

      // In cascade mode, transition cascaded wild symbols to "win" animation
      // ONLY if they are part of an actual win line (non-winning wilds stay as static texture)
      if (isCascadeMode && expandingWildReelIndices.length > 0) {
        expandingWildReelIndices.forEach((reelIndex) => {
          const overlayReelController =
            reelsetControllerRef.current.overlayReelControllers[reelIndex];
          const reelController =
            reelsetControllerRef.current.reelControllers[reelIndex];

          if (!overlayReelController || !reelController) return;

          const winningPositionsOnReel = winPositionsByReel.get(reelIndex);

          for (
            let symbolIndex = 0;
            symbolIndex < GAME_LAYOUT.SYMBOLS_VISIBLE;
            symbolIndex++
          ) {
            // Only animate wilds that are part of a winning line
            if (
              !winningPositionsOnReel ||
              !winningPositionsOnReel.has(symbolIndex)
            ) {
              continue;
            }

            const symbol =
              reelController.symbolsContainer.children[symbolIndex];
            if (symbol) {
              const actualSymbolId = symbol.currentSymbolId || symbol.symbolId;

              if (actualSymbolId === WILD_SYMBOL_ID) {
                const symbolWinMs = SpineAnimationData.getSymbolWinDuration(actualSymbolId, useFastWin);
                const perSymbolLoops = (symbolWinMs && symbolWinMs > 0 && displayWindowMs > 0)
                  ? Math.max(1, Math.round(displayWindowMs / symbolWinMs))
                  : 1;
                overlayReelController.showWinAnimation(
                  symbolIndex,
                  actualSymbolId,
                  winAnimationType,
                  null,
                  perSymbolLoops,
                );
              }
            }
          }
        });
      }

      // === STEP 4: Hide textures behind spines (reveal spine animations) ===
      for (let i = 0; i < allPositions.length; i++) {
        const [reelIndex, symbolIndex] = allPositions[i];
        const reelController =
          reelsetControllerRef.current.reelControllers[reelIndex];
        const overlayReelController =
          reelsetControllerRef.current.overlayReelControllers[reelIndex];
        const hasExpandingWildFullReel =
          overlayReelController &&
          FEATURES_CONFIG.EXPANDING_WILDS.ENABLED &&
          FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel" &&
          overlayReelController.isExpandingWildVisible === true;

        if (hasExpandingWildFullReel) {
          continue;
        }

        // ── A Golden cell's texture IS its picture. Never hide it ──
        //
        // This step clears the flat symbol so the spine started above shows
        // through. A Golden cell has no such spine: the feature owns the cell
        // end to end, and what it leaves behind is a composite on the reel —
        // frame, window and outcome baked into one texture.
        //
        // Hidden, that cell goes blank. It is reachable through the TRIGGER
        // win, whose positions are the scatters wherever they came from, and a
        // scatter the feature dropped is a Golden cell. Nothing was drawn over
        // it, so the row simply emptied.
        if (overlayReelController?.hasGoldenMystery?.(symbolIndex)) {
          continue;
        }

        if (reelController && reelController.hideSymbolTexture) {
          reelController.hideSymbolTexture(symbolIndex);
        }
      }

      // === STEP 5: Show win lines (on top of active spines) ===
      // === STEP 5: Show win lines (on top of active spines) ===
      if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
        const regularLineIds = lineIds
          .map((key) => winningData[key]?.lineId)
          .filter((id) => id !== undefined && id !== null);
        if (regularLineIds.length > 0) {
          winLinesContainerRef.current.showMultipleWinLines(regularLineIds);
        }
      }

      // === STEP 5b: Show win boxes on all line positions ===
      if (FEATURES_CONFIG.WIN_BOX.ENABLED && winBoxContainerRef.current) {
        const allLinePositions = [];
        lineIds.forEach((key) => {
          const data = winningData[key];
          // Skip trigger entries — no winboxes for synthetic wins.
          //
          // Scatters join coins here. A win box frames a PAYING combination,
          // and a trigger entry pays 0: it exists only so the symbols animate
          // and the feature screen has something to open from. Boxing it tells
          // the player they were paid for the scatters, which never happens.
          if (data?.isCoin || data?.isScatter) return;
          // ── And a feature's own payout is not boxed either ──
          //
          // For the opposite reason to the triggers above: not because it pays
          // nothing, but because the cells it pays for are already the whole
          // presentation. The Golden Mystery's coins stand on the board with
          // their values on them; framing them adds a second, quieter statement
          // of the same thing, and the reference game does not.
          if (data?.isFeaturePayout) return;
          if (FEATURES_CONFIG.WIN_BOX.WINNING_ONLY && data?.positions?.length > 0) {
            // WINNING_ONLY: use only matched symbol positions
            allLinePositions.push(data.positions);
          } else if (data?.line) {
            // Full line path
            allLinePositions.push(
              data.line.map((row, reelIndex) => [reelIndex, row]),
            );
          } else if (data?.positions?.length > 0) {
            // Scatter win (no line) — use actual positions directly
            allLinePositions.push(data.positions);
          }
        });
        if (allLinePositions.length > 0) {
          // Show-all-wins: compute win box loops from display window (same timing as symbols)
          const winBoxAnimMs = SpineAnimationData.getWinBoxDuration();
          const winBoxLoops = (FEATURES_CONFIG.WIN_BOX.SNAP_TO_LOOP && winBoxAnimMs > 0 && displayWindowMs > 0)
            // ? Math.max(1, Math.floor(displayWindowMs / winBoxAnimMs))
            // Rounded, not floored: 2000 / 666.7 = 2.9998 floored to 2 loops
            // and the box froze on its last frame for the last third of the
            // global win (Vladimir, 2026-09-30). Round fills the window.
            ? Math.max(1, Math.round(displayWindowMs / winBoxAnimMs))
            : 0;
          // winBoxContainerRef.current.showAllWinBoxes(allLinePositions, winBoxLoops);
          winBoxContainerRef.current.showAllWinBoxes(allLinePositions, winBoxLoops, displayWindowMs);
        }
      }

      // === STEP 6: Show win text (overlay on top of everything) ===
      if (globalLineWinTextRef?.current && currentRoundRef.current?.win) {
        const winTextOptions = {};
        // Speed up WinText to fit in→idle→out within fast win duration
        if (useFastWin && winFastConfig?.WIN_TEXT_SPEED_UP) {
          winTextOptions.targetDuration =
            FEATURES_CONFIG.WIN_ANIMATION_TIMING.SPINE_LOOP_DURATION; // 1000ms (matches win_fast)
        }
        globalLineWinTextRef.current.showWinAmount(currentRoundRef.current.win, winTextOptions);
      }

      // === STEP 7: Dim reels LAST (everything is ready, dim reveals the full composition) ===
      //
      // ...unless a trigger's reveal has not decided yet. Dimming when three
      // scatters land presents a win before the strip has chosen anything, and
      // for a blank it presents one that never comes. The dim moved into the
      // reveal block in cycleThroughLines, which fires it only once the strip
      // has landed AND a package was actually awarded.
      if (
        dimReelSetRef.current &&
        !dimHeldForReveal(currentRoundRef.current)
      ) {
        dimReelSetRef.current.dimReels(true);
      }

      if (
        FEATURES_CONFIG.EXPANDING_WILDS.ENABLED &&
        FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel" &&
        expandingWildReelIndices.length > 0
      ) {
        expandingWildReelIndices.forEach((reelIndex) => {
          const overlayReelRef = overlayReelRefs.current?.[reelIndex];
          overlayReelRef?.playExpandingWildWinAnimation?.();
          expandingWildAnimationStateRef.current[reelIndex] = true;
        });
      }

      // Event-based timing: use pre-cached durations from SpineAnimationData
      await new Promise((resolve) => {
        const masterDuration = SpineAnimationData.getMasterWinDuration();
        const fastWinDuration = SpineAnimationData.getFastWinDuration();

        // The GLOBAL show-all hold — how long every winning symbol stays up
        // before line cycling. Routed through winLoops so a fast win gets
        // WIN_FAST.LOOPS here too; this is the duration a show-all round
        // actually displays for, and leaving it at one loop of `win_fast` is
        // what made the win a flash.
        const targetLoops = winLoops(
          useFastWin,
          hasExpandingWilds
            ? FEATURES_CONFIG.EXPANDING_WILDS
                .LOOPS_FOR_EXPANDING_WILD_SHOW_ALL
            : showAllNormalLoops,
        );

        const activeDuration = (useFastWin && fastWinDuration !== null)
          ? fastWinDuration
          : masterDuration;
        const displayDuration = activeDuration * targetLoops;

        // Unblock spin button — timing depends on input mode:
        // Manual mode: unblock after SPIN_UNBLOCK_DELAY (1000ms) so user can hold spacebar to turbo.
        // Autoplay / spacebar turbo: unblock at full displayDuration so animations complete cleanly before next spin.
        let unblockDelay;
        const currentState = store.getState();
        const isAutoplay = currentState.settings.autoPlayActive;

        if (spacebarTurboActiveRef.current || isAutoplay) {
          // Both spacebar turbo and autoplay: unblock at full display duration.
          // The continuous effect fires immediately on counterAnimationFinished — no extra SPIN_DELAY needed.
          unblockDelay = displayDuration;
        } else {
          // Manual mode: fixed delay so user can hold spacebar to turbo after 1s
          unblockDelay = FEATURES_CONFIG.WIN_ANIMATION_TIMING.SPIN_UNBLOCK_DELAY;
        }

        const unblockTimeoutId = new TickerTimeout(() => {
          const idx = currentTimeoutsRef.current.indexOf(unblockTimeoutId);
          if (idx > -1) currentTimeoutsRef.current.splice(idx, 1);
          dispatch(setCounterAnimationFinished(true));
        }, unblockDelay);
        currentTimeoutsRef.current.push(unblockTimeoutId);

        // Full display duration — animations keep playing after spin unblocks
        const displayTimeoutId = new TickerTimeout(() => {
          const index = currentTimeoutsRef.current.indexOf(displayTimeoutId);
          if (index > -1) {
            currentTimeoutsRef.current.splice(index, 1);
          }
          if (!holdForPopup) {
            resolve();
            return;
          }
          // The popup is up: keep the pass looping. Once it closes, run the
          // current pass out to its boundary — no cut mid-loop — then hand
          // over to the scatter line. Every timer is on currentTimeoutsRef,
          // so a spin start clears the whole wait.
          const finishPass = () => {
            if (selectWinAnimationsPlaying(store.getState())) {
              currentTimeoutsRef.current.push(new TickerTimeout(finishPass, 100));
              return;
            }
            const elapsed = performance.now() - passStartedAt;
            const remainder =
              activeDuration > 0 ? activeDuration - (elapsed % activeDuration) : 0;
            currentTimeoutsRef.current.push(new TickerTimeout(resolve, remainder));
          };
          finishPass();
        }, displayDuration);
        currentTimeoutsRef.current.push(displayTimeoutId);
      });

      // Release the early animation lock so startWinningLinesAnimation can take over
      isAnimatingRef.current = false;

      // Use spacebarTurboActiveRef (synchronous) instead of turboActiveRef (useEffect-batched).
      // turboActiveRef can be stale if user released spacebar but React hasn't re-rendered yet.


      // A held spacebar normally means "you have seen enough, move on", and
      // returning false is what stops the line cycle. A trigger has to run it
      // anyway — see isTriggerPresentationRound.
      if (spacebarTurboActiveRef.current && !isTriggerPresentationRound()) {
        return false;
      }

      return true;
    },
    [
      globalLineWinTextRef,
      dimHeldForReveal,
      dimReelSetRef,
      winLinesContainerRef,
      winBoxContainerRef,
      overlayReelRefs,
      isTriggerPresentationRound,
      dispatch,
      spacebarTurboActiveRef,
    ],
  );



  /**
   * Starts the win line animation cycle.
   * Cycles through each winning line, showing animations and win amounts.
   * Feature trigger lines are moved to the END — see orderLineIds.
   */
  const startWinningLinesAnimation = useCallback(
    async (winningData) => {
      if (isAnimatingRef.current) {
        return;
      }

      const lineIds = orderLineIds(winningData);
      const winTypeLocal = winType;
      const isAnimatedWinLocal = isAnimatedWin;

      // ── Does this board hold a complete trigger? ──
      //
      // Asked of the WINNING DATA, not of a flag, and that is the whole point.
      //
      // Two flags were tried and both failed on a live round. `freeSpins
      // .triggered` is the AWARD half — false on a blank, by design. And
      // `triggerReveal` is stamped by only SOME of the paths that build a
      // round: the reconstruct path at RGS.js:1271 appends the synthetic
      // scatter win and never stamps it, so a real spin arrived here with the
      // trigger in its wins and `triggerReveal: undefined`. The dev rounds
      // hardcode `triggerReveal: true`, which is exactly why the simulation
      // always looked right and the live game never did.
      //
      // The synthetic scatter win IS the evidence, and it is already in the
      // data this function was handed. It is built in one place, only inside
      // `triggerPositions.length > 0`, and every producer drops a pre-existing
      // scatter win before appending it — so one `isScatter` entry means a
      // complete trigger board and nothing else can forge one.
      //
      // `isScatter` only. `isCoin` is Royal Collect's and Pot Collection's
      // trigger and belongs to their own branches below.
      const hasScatterTrigger = lineIds.some(
        (id) => !!winningData[id]?.isScatter,
      );


      if (winTypeLocal && !winSoundPlayedRef.current) {
        if (isAnimatedWinLocal) {
          winSoundPlayedRef.current = true;
        }
      }

      isAnimatingRef.current = true;
      // Force-complete counter state before clearing timeouts.
      // clearAllTimeouts() kills the pending unblock timeout (line 2093-2097)
      // that would have dispatched counterAnimationFinished(true). Without this,
      // the turbo Continuous Effect stalls waiting for a transition that never comes.
      const animState = store.getState().animations;
      if (animState.counterAnimationStarted && !animState.counterAnimationFinished) {
        dispatch(setCounterAnimationFinished(true));
      }
      clearAllTimeouts();

      if (globalLineWinTextRef?.current) {
        globalLineWinTextRef.current.hide();
      }

      // Clear previous win positions for clean first-line diff
      prevWinPosSetRef.current = null;

      // Held back while a trigger's reveal is still to come — same rule as
      // STEP 7 in showAllWinningSymbolsOnce. The reveal block in
      // cycleThroughLines owns the dim for a trigger.
      if (
        dimReelSetRef.current &&
        !dimHeldForReveal(currentRoundRef.current)
      ) {
        dimReelSetRef.current.dimReels(true);
      }

      winningDataRef.current = winningData;
      lineIdsRef.current = lineIds.slice();
      currentLineIndexRef.current = 0;

      const runSeamlessAnimationCycle = async () => {
        while (isAnimatingRef.current) {
          try {
            // On first cycle, pass callback to unblock spin after first line completes
            const unblockCallback = isFirstAnimationRef.current
              ? () => dispatchRef.current(setCounterAnimationFinished(true))
              : null;

            const playedAny = await cycleThroughLines(
              winningData,
              lineIds,
              isFirstAnimationRef.current,
              0,
              unblockCallback,
            );

            // ── A pass that played NOTHING ends the loop ──
            //
            // Not a safety net — a real case. A blank gamble scatter leaves a
            // synthetic scatter entry in `winningData` that the cycle skips
            // (its doors are already open and it pays nothing). When it is the
            // ONLY entry, every pass skips everything, returns instantly, and
            // this while loop runs flat out with no await in it: the tab
            // freezes with nothing in the console.
            //
            // Looping forever is right when there is something to show — that
            // is the rule for every win in this game. With nothing to show
            // there is no cycle, only a spin.
            if (!playedAny) {
              isAnimatingRef.current = false;
              break;
            }

            isFirstAnimationRef.current = false;
            currentLineIndexRef.current = 0;
          } catch (error) {
            // SAFETY NET: If the animation cycle errors out before onFirstLineComplete fired,
            // dispatch counterAnimationFinished(true) to prevent permanent spin button lockup.
            // Without this, the button stays blocked forever because winNotFullyDisplayed stays true.
            if (isFirstAnimationRef.current) {
              dispatchRef.current(setCounterAnimationFinished(true));
              isFirstAnimationRef.current = false;
            }
            break;
          }
        }
      };

      // FREE SPIN TRIGGER: Play lines linearly once (0 → scatter) and stop.
      // Scatter is always the last line, so we end on it naturally.
      //
      // ── Why `freeSpins.triggered` alone was the wrong test ──
      //
      // That flag is the AWARD half. RGS emits `freeSpinsTrigger` only when
      // hasFreeSpinAwardEvidence passed, so it is FALSE on a blank — the
      // provider says so in as many words where it stamps triggerReveal.
      //
      // So a round that paid a payline AND put the full trigger board on
      // screen fell through to runSeamlessAnimationCycle whenever the strip
      // was going to land on a BLANK: the global win showed, and then the
      // player watched every payline cycle one at a time before the doors
      // opened. On an awarded round the branch ran and the doors opened at
      // once — which is why the dev simulation, whose rounds always award,
      // never reproduced it.
      //
      // `triggerReveal` is the PRESENTATION half: true whenever SC1 is on the
      // board, whatever it goes on to land on. It is the half that owes the
      // doors, so it is the half this branch asks about. Same reasoning, and
      // the same pair of facts, as isTriggerPresentationRound above.
      if (
        FEATURES_CONFIG.FREE_SPINS.ENABLED &&
        (store.getState().freeSpins.triggered ||
          currentRoundRef.current?.triggerReveal === true ||
          hasScatterTrigger)
      ) {
        // Ensure animated win is tracked — for scatter-only rounds (4-5 scatters),
        // showAllWinningSymbolsOnce is skipped so winAnimationsPlaying was never set.
        // Without this, the scatter detection wait in cycleThroughLines can't catch it.
        //
        // ── Once per round ──
        //
        // WinAnimation STARTS the celebration on every rising edge of this flag.
        // When show-all had already played the celebration and it had closed
        // (or been tapped away) before this branch ran, raising it again played
        // the whole big win a second time. Vladimir, 2026-09-24: *"2 big wins
        // showing consecutively."* So only a round whose celebration was never
        // started gets it here.
        if (isAnimatedWin && !celebrationRaisedRef.current) {
          celebrationRaisedRef.current = true;
          dispatchRef.current(setWinAnimationsPlaying(true));
        }

        const wasFirstAnimation = isFirstAnimationRef.current;
        const unblockCallback = wasFirstAnimation
          ? () => dispatchRef.current(setCounterAnimationFinished(true))
          : null;

        // When show-all-wins is active, all payline wins were already displayed
        // simultaneously — skip straight to the trigger line (always last).
        const triggerStartIndex = FEATURES_CONFIG.SHOW_ALL_WINS.ENABLED
          ? lineIds.length - 1
          : 0;

        try {
          await cycleThroughLines(
            winningData,
            lineIds,
            isFirstAnimationRef.current,
            triggerStartIndex,
            unblockCallback,
          );
        } catch (error) {
          // SAFETY NET: Same as runSeamlessAnimationCycle — ensure spin button unblocks on error
          if (isFirstAnimationRef.current) {
            dispatchRef.current(setCounterAnimationFinished(true));
          }
        }
        isFirstAnimationRef.current = false;

        // ── AND IF NOTHING WAS TRIGGERED, IT IS AN ORDINARY WIN AFTER ALL ──
        //
        // This branch is the TRIGGER presentation: show-all shows the paylines
        // once, the cycle jumps straight to the scatters, and it STOPS there
        // because the feature is about to take the screen. Right for a trigger,
        // and only for a trigger.
        //
        // It has to be ENTERED before the answer is known — `triggerReveal`
        // means a reveal is owed, and the doors are opened from inside this
        // cycle — but by the time that await returns the strip HAS landed and
        // the answer is known. A blank awards nothing, no feature is coming,
        // and stopping here leaves the win frozen with nothing to take it.
        // Vladimir, 2026-09-18: *"we should only display basic win infinitely
        // because there is nothing to be triggered."*
        //
        // So a settled blank falls through to the seamless cycle below, and the
        // round finishes its life as what it actually is — a basic win.
        // ── Read the ROUND, not the cycle's own refs ──
        //
        // `revealDoneForRoundRef` and `revealAwardedForRoundRef` are set by the
        // reveal that runs INSIDE this cycle. A gamble scatter dropped by the
        // Golden Mystery has its doors opened earlier, by the feature's own
        // block, so both refs stay empty and a test built on them can never be
        // true for exactly the case it was written for. Proved by the trace:
        // `revealDone=- revealAwarded=-`.
        //
        // The round carries the answer and has since the spin resolved:
        // `gambleScatter` is the reveal payload, and a blank is value 0 and
        // tier 0. That is true wherever the scatter came from and whoever
        // opened it.
        const settledPackage = currentRoundRef.current?.gambleScatter;
        const settledBlank =
          !!settledPackage &&
          (settledPackage.value ?? 0) <= 0 &&
          (settledPackage.tier ?? 0) <= 0;
        if (!settledBlank) return;

        // ── The blank still owes the spin its unblock ──
        //
        // `unblockCallback` above is handed to cycleThroughLines, which fires
        // it at `index === 0` and nowhere else. This branch starts at
        // `triggerStartIndex` — the LAST line, whenever SHOW_ALL_WINS is on —
        // so index 0 is reached only when the round had exactly ONE winning
        // line. Two or more and the callback never runs.
        //
        // For a real trigger that is correct: the feature takes the screen and
        // unblocks on its own way in. A blank has no feature coming, and
        // nothing downstream will do it — `isFirstAnimationRef` is already
        // false by here, so the seamless cycle below builds a null callback
        // too. The counter then never finishes, and AUTOPLAY waits on exactly
        // that (isPresentationHoldingScreen).
        //
        // Vladimir, 2026-09-18: autoplay carried on after one blank and
        // stopped dead on the next. That is this — one line versus two.
        //
        // Safe to dispatch twice: it is an assignment, and the single-line
        // case has already set the same flag to the same value.
        if (wasFirstAnimation) {
          dispatchRef.current(setCounterAnimationFinished(true));
        }
      }

      // ROYAL COLLECT TRIGGER: Play lines linearly once (0 → coin trigger) and stop.
      // Coin trigger is always the last line, so we end on it naturally — same as free spin scatters.
      if (
        FEATURES_CONFIG.ROYAL_COLLECT.ENABLED &&
        store.getState().featureGame.royalCollectTriggered
      ) {
        if (isAnimatedWin) {
          dispatchRef.current(setWinAnimationsPlaying(true));
        }

        const unblockCallback = isFirstAnimationRef.current
          ? () => dispatchRef.current(setCounterAnimationFinished(true))
          : null;

        // When show-all-wins is active, skip straight to the trigger line (always last).
        const triggerStartIndex = FEATURES_CONFIG.SHOW_ALL_WINS.ENABLED
          ? lineIds.length - 1
          : 0;

        try {
          await cycleThroughLines(
            winningData,
            lineIds,
            isFirstAnimationRef.current,
            triggerStartIndex,
            unblockCallback,
          );
        } catch (error) {
          if (isFirstAnimationRef.current) {
            dispatchRef.current(setCounterAnimationFinished(true));
          }
        }
        isFirstAnimationRef.current = false;
        return;
      }

      // POT COLLECTION TRIGGER: Same pattern as RC — play lines linearly once.
      if (
        (FEATURES_CONFIG.POT_COLLECTION.ENABLED &&
          store.getState().potCollection.potCollectionTriggered) ||
        selectHoldAndWinIntroPending(store.getState()) // Hold & Win: same cycle
      ) {
        if (isAnimatedWin) {
          dispatchRef.current(setWinAnimationsPlaying(true));
        }

        const unblockCallback = isFirstAnimationRef.current
          ? () => dispatchRef.current(setCounterAnimationFinished(true))
          : null;

        // When show-all-wins is active, skip straight to the trigger line (always last).
        const triggerStartIndex = FEATURES_CONFIG.SHOW_ALL_WINS.ENABLED
          ? lineIds.length - 1
          : 0;

        try {
          await cycleThroughLines(
            winningData,
            lineIds,
            isFirstAnimationRef.current,
            triggerStartIndex,
            unblockCallback,
          );
        } catch (error) {
          if (isFirstAnimationRef.current) {
            dispatchRef.current(setCounterAnimationFinished(true));
          }
        }
        isFirstAnimationRef.current = false;
        return;
      }

      runSeamlessAnimationCycle();
    },
    [
      clearAllTimeouts,
      cycleThroughLines,
      dimHeldForReveal,
      dimReelSetRef,
      dispatch,
      globalLineWinTextRef,
      orderLineIds,
      winType,
      isAnimatedWin,
    ],
  );

  const cycleThroughLinesRef = useRef(null);
  cycleThroughLinesRef.current = cycleThroughLines;

  const showAllWinningSymbolsOnceRef = useRef(null);
  showAllWinningSymbolsOnceRef.current = showAllWinningSymbolsOnce;



  const startWinningLinesAnimationRef = useRef(null);
  startWinningLinesAnimationRef.current = startWinningLinesAnimation;



  /**
   * Resets all animations and effects when a new spin starts.
   */
  useEffect(() => {
    if (bonusBoardActive) {
      return;
    }

    if (!isSpinning || !reelsetControllerRef.current) return;

    const wasAnimating = isAnimatingRef.current;

    // The scene's held reaction goes with everything else the win put up — see
    // the playAccent call in the presentation. Mixed out, so the rat's bones
    // return to the idle rather than snapping to it.
    gameViewControllerRef?.current?.stopAccent();

    // ── Synchronous visual cleanup ──────────────────────────────────

    // Must complete in the SAME frame before reels start spinning.
    // Previously resetAllEffects(true) was async (100ms undim fade),
    // which let reels begin while dim overlay was still fading.

    // 1. Snap dim overlay off instantly (no fade)
    if (dimReelSetRef.current?.snapClear) {
      dimReelSetRef.current.snapClear();
    }

    // 2. Hide win lines
    if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
      winLinesContainerRef.current.hideWinLine();
    }

    // 3. Hide win boxes
    if (FEATURES_CONFIG.WIN_BOX.ENABLED && winBoxContainerRef.current) {
      winBoxContainerRef.current.hideWinBoxes();
    }

    // 4. Hide overlay reel animations
    executeOnOverlayReelControllersRef.current((oc) => {
      if (oc?.hideAllAnimations) oc.hideAllAnimations();
      if (FEATURES_CONFIG.EXPANDING_WILDS.ENABLED && oc?.isExpandingWildVisible) {
        oc.forceStopExpandingWildFullReel?.();
      }
    });

    // 5. Restore symbol textures
    executeOnReelControllersRef.current((c) => {
      if (c?.showAllSymbolTextures) c.showAllSymbolTextures();
    });

    // 6. Hide line win text
    if (lineWinTextRef?.current) lineWinTextRef.current.hide();
    // NOTE: globalLineWinTextRef is intentionally NOT hidden here.
    // It self-cleans via its spine "out" animation → _finalizeOut().
    // If a new win arrives, showWinAmount() replaces it seamlessly.

    // 7. Clear win position tracking
    prevWinPosSetRef.current = null;

    // ── End visual cleanup ──────────────────────────────────────────

    // Always clear all timeouts — catches orphaned timeouts from prior cycles
    clearAllTimeouts();

    if (wasAnimating) {
      isAnimatingRef.current = false;
    }

    dispatch(setWinAnimationsPlaying(false));
    dispatch(setIsAnimatedWin(false));
    dispatch(setCriticalAnimationPlaying(false));
    dispatch(setExpandingWildsOccurred(false));
    dispatch(setSkipWinAnimation(false));
    dispatch(setUserWantsToDismissWinAnimation(false));
    dispatch(setSkipCounterAnimation(false));
    dispatch(setIsWaitingForUserConfirmation(false));

    animationCompletedRef.current = false;
    fastPathRoundRef.current = null; // Clear fast path tag — new spin starts clean
    isFirstAnimationRef.current = true;
    animatedRoundsRef.current.clear();
    winSoundPlayedRef.current = false;
    celebrationRaisedRef.current = false;
    expandingWildReelIndicesRef.current = [];
    expandingWildAnimationStateRef.current = {};
    winningDataRef.current = null;
    lineIdsRef.current = [];
    currentLineIndexRef.current = 0;

    useFastWinRef.current = false;

    soundManager.stop(SOUND_NAME.expanding_wild);
    if (FEATURES_CONFIG.WIN_LINES.ENABLED) {
      soundManager.stop(SOUND_NAME.win_line);
    }
  }, [
    isSpinning,
    // Stable context ref — see the note on the effect above.
    gameViewControllerRef,

    freeSpinActive,
    bonusBoardActive,
    clearAllTimeouts,
    dispatch,
    dimReelSetRef,
    winLinesContainerRef,
    winBoxContainerRef,
    overlayReelRefs,
    globalLineWinTextRef,
    lineWinTextRef,
  ]);

  /**
   * Stops the base presentation the moment the ROOM takes the screen.
   *
   * ── The bug this exists for ──
   *
   * A round can pay a line win AND trigger the bonus. The wins present first
   * and the trigger last (see orderLineIds), so when the wipe comes the base
   * game is normally finished — but `cycleThroughLines` is an async loop, not
   * something the effect owns. Its only exit is `isAnimatingRef`, and the main
   * effect merely REFUSES to start a new presentation once `bonusBoardActive`
   * goes true; it cannot stop one already running.
   *
   * So a cycle in flight kept going inside the room: win lines, win boxes and
   * symbol animations for money the base game had already paid, over a board
   * that had nothing to do with it. The win boxes and lines are the visible
   * part, because their containers are shared through ReelRefsContext and draw
   * above whichever scene owns the screen.
   *
   * The free-spins controller was never the culprit and could not have been —
   * it blocks the arrival board on two separate signals. This is the base game
   * failing to let go.
   *
   * ── Why a second effect and not a flag on the first ──
   *
   * The existing reset below fires on `resetMainGameVisualEffects`, which the
   * feature dispatches on the way OUT. Nothing dispatched anything on the way
   * IN. Same teardown, the other direction, so the two read as a pair.
   *
   * Deliberately NOT clearing animatedRoundsRef: the trigger round has to stay
   * marked as presented, or coming back from the room re-presents it — which is
   * the same fault by another route.
   */
  useEffect(() => {
    if (!bonusBoardActive) return;

    if (isAnimatingRef.current) {
      isAnimatingRef.current = false;
      clearAllTimeouts();
    }

    reelsetControllerRef.current?.resetAllEffects?.();

    if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
      winLinesContainerRef.current.hideWinLine();
    }
    if (FEATURES_CONFIG.WIN_BOX.ENABLED && winBoxContainerRef.current) {
      winBoxContainerRef.current.hideWinBoxes();
    }
    if (lineWinTextRef?.current) lineWinTextRef.current.hide();
    // A win + trigger round leaves the scene's reaction looping behind a hidden
    // base view otherwise — invisible, but still ticking.
    gameViewControllerRef?.current?.stopAccent();

    baseDiag("winController -> room owns the screen, base cycle stopped");
  }, [
    bonusBoardActive,
    clearAllTimeouts,
    // A context ref — a stable identity, so listing it satisfies the linter
    // without changing when this effect runs.
    gameViewControllerRef,
    winLinesContainerRef,
    winBoxContainerRef,
    lineWinTextRef,
  ]);

  /**
   * Resets visual effects when returning from free spins feature.
   */
  useEffect(() => {
    if (resetMainGameVisualEffects) {
      if (reelsetControllerRef.current?.reelControllers) {
        reelsetControllerRef.current.resetAllEffects();

        if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
          winLinesContainerRef.current.hideWinLine();
        }

        if (FEATURES_CONFIG.WIN_BOX.ENABLED && winBoxContainerRef.current) {
          winBoxContainerRef.current.hideWinBoxes();
        }
      }

      if (isAnimatingRef.current) {
        isAnimatingRef.current = false;
        clearAllTimeouts();
      }

      animationCompletedRef.current = false;
      isFirstAnimationRef.current = true;
      animatedRoundsRef.current.clear();


      dispatch(setResetMainGameVisualEffects(false));
    }
  }, [
    resetMainGameVisualEffects,
    clearAllTimeouts,
    dispatch,
    winLinesContainerRef,
    winBoxContainerRef,
    ]);

  /**
   * Shows turbo suggestion popup on non-winning rounds when appropriate.
   */
  useEffect(() => {
    if (!FEATURES_CONFIG.TURBO_SUGGESTION.ENABLED) {
      return;
    }

    if (
      turboSuggestionPending &&
      spinComplete &&
      !isSpinning &&
      !freeSpinActive
    ) {
      if (!hasWins) {
        // Check if user previously declined — don't show again
        const uiSettings = StorageManager.loadUISettings();
        if (!uiSettings.turboSuggestionDeclined) {
          dispatch(openTurboSuggestionPopup());
        }
        dispatch(setTurboSuggestionPending(false));
      }
    }
  }, [
    turboSuggestionPending,
    spinComplete,
    isSpinning,
    freeSpinActive,
    bonusBoardActive,
    currentRound,
    dispatch,
    hasWins,
  ]);

  /**
   * Cleanup effect: Clears all timeouts and resets effects when component unmounts.
   */
  useEffect(() => {
    return () => {
      clearAllTimeouts();
      isAnimatingRef.current = false;

      if (reelsetControllerRef.current) {
        reelsetControllerRef.current.resetAllEffects();
      }
    };
  }, [clearAllTimeouts]);

  return {
    reelsetController: reelsetControllerRef.current,
  };
};

export default useWinController;
